/**
 * ESS POS — Stage 6: Restaurant Owner & ESS Super Admin Workspaces
 *
 * Strictly separates:
 * A. Restaurant Owner Workspace (Tenant-scoped business settings, M-Pesa subscription renewal,
 *    menu/product configuration with S3 object-storage image upload, multi-branch & staff management)
 * B. ESS Super Admin Workspace (Platform-level tenant management, SaaS subscription plans,
 *    payment reconciliation, API/cluster health & system-wide audit logs)
 */

import { apiClient } from '../api/apiClient';
import { restaurantMockAdapter } from '../api/mockAdapter';
import {
  bindImageUploadControl,
  escapeHtml,
  formatCurrency,
  formatDateTime,
  openModal,
  renderEmptyState,
  renderErrorState,
  renderImageUploadControl,
  renderLoadingState,
  showToast,
} from '../components/ui';
import { openAddStaffModal } from './managerWorkspace';

// ============================================================================
// PART A: RESTAURANT OWNER WORKSPACE (Tenant-Scoped)
// ============================================================================

export async function renderOwnerWorkspace(
  viewport: HTMLElement,
  activeTab: string,
  onNavigateTab: (tabId: string) => void
) {
  viewport.innerHTML = renderLoadingState('Loading Restaurant Owner Executive Suite...');

  try {
    const [
      profile,
      branches,
      locations,
      categories,
      products,
      staff,
      subStatus,
      plans,
      invoices,
      dailySales,
      valuation,
    ] = await Promise.all([
      apiClient.getTenantProfile(),
      apiClient.listBranches(),
      apiClient.listStockLocations(),
      apiClient.listCategories(),
      apiClient.listProducts(),
      apiClient.listStaff(),
      apiClient.getSubscriptionStatus(),
      apiClient.listSubscriptionPlans(),
      apiClient.listInvoices(),
      apiClient.getDailySalesReport(),
      apiClient.getInventoryValuationReport(),
    ]);

    const tenantId = apiClient.getEffectiveTenantId();
    const auditLogs = restaurantMockAdapter.listAuditLogs(tenantId);
    const refresh = () => renderOwnerWorkspace(viewport, activeTab, onNavigateTab);

    if (activeTab === 'subscription') {
      renderOwnerSubscriptionScreen(viewport, {
        subStatus,
        plans,
        invoices,
        onRefresh: refresh,
      });
    } else if (activeTab === 'menu-config') {
      renderOwnerMenuConfigScreen(viewport, {
        categories,
        products,
        locations,
        onRefresh: refresh,
      });
    } else if (activeTab === 'branches-staff') {
      renderOwnerBranchesAndStaffScreen(viewport, {
        profile,
        branches,
        locations,
        staff,
        onRefresh: refresh,
      });
    } else if (activeTab === 'settings-audit') {
      renderOwnerSettingsAndAuditScreen(viewport, {
        profile,
        auditLogs,
      });
    } else {
      // Default: 'executive'
      renderOwnerExecutiveDashboard(viewport, {
        profile,
        subStatus,
        dailySales,
        valuation,
        branches,
        staff,
        onNavigateTab,
        onRefresh: refresh,
      });
    }
  } catch (err: any) {
    viewport.innerHTML = renderErrorState({
      title: 'Failed to Load Restaurant Owner Workspace',
      message: err?.message || 'Unable to load tenant profile or executive metrics.',
      retryButtonId: 'owner-retry-btn',
    });
    viewport.querySelector('#owner-retry-btn')?.addEventListener('click', () => {
      renderOwnerWorkspace(viewport, activeTab, onNavigateTab);
    });
  }
}

function renderOwnerExecutiveDashboard(
  viewport: HTMLElement,
  params: {
    profile: any;
    subStatus: any;
    dailySales: any;
    valuation: any;
    branches: any[];
    staff: any[];
    onNavigateTab: (tab: string) => void;
    onRefresh?: () => void;
  }
) {
  const tenant = params.profile?.tenant;
  const plan = params.subStatus?.plan;
  const menuSlug = tenant?.easy_menu_slug || tenant?.slug || 'sip-chat-cafe';
  const secretKey = tenant?.secret_key || tenant?.secretKey || `ess_sec_${(tenant?.slug || 'sip_chat_cafe').replace(/[^a-z0-9]/g, '_')}_${(tenant?.id || '').slice(0, 8)}`;

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">${escapeHtml(tenant?.name || 'Restaurant')} &mdash; Executive Overview</h2>
          <p class="ess-section-desc">Authorized Tenant Slug: <strong>${escapeHtml(tenant?.slug)}</strong> &bull; Plan: <strong>${escapeHtml(plan?.name || 'ESS Plan')}</strong></p>
        </div>
        <div class="ess-btn-group" style="flex-wrap:wrap;gap:8px;">
          <button type="button" class="ess-btn ess-btn-primary" id="own-top-sync-easymenu" style="background:var(--ess-orange-primary,#f97316);color:#fff;font-weight:700;display:inline-flex;align-items:center;gap:6px;box-shadow:0 2px 8px rgba(249,115,22,0.3);">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            ⚡ EasyMenu Sync
          </button>
          <button type="button" class="ess-btn ess-btn-secondary" id="own-go-menu">Menu &amp; Dishes</button>
          <button type="button" class="ess-btn ess-btn-secondary" id="own-go-sub">Subscription &amp; Billing</button>
        </div>
      </div>

      <!-- EasyMenu Digital Menu & Live QR Table Ordering Card -->
      <div class="ess-card" style="border:2px solid var(--ess-orange-primary,#f97316);background:rgba(249,115,22,0.03);padding:20px;border-radius:12px;">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:16px;">
          <div style="max-width:620px;">
            <div style="font-size:1.15rem;font-weight:800;color:var(--ess-text-primary);display:flex;align-items:center;gap:8px;">
              <span>⚡ EasyMenu Digital Catalog &amp; Live QR Ordering</span>
              <span class="ess-badge ess-badge-success">LIVE LINKED</span>
            </div>
            <div style="font-size:0.85rem;color:var(--ess-text-secondary);margin-top:6px;line-height:1.5;">
              Public QR Menu: <a href="https://easy-scan.shop/r/${escapeHtml(menuSlug)}" target="_blank" rel="noopener noreferrer" style="color:var(--ess-orange-primary);font-weight:700;text-decoration:none;">https://easy-scan.shop/r/${escapeHtml(menuSlug)} &nearr;</a><br>
              Table orders placed on easy-scan.shop stream directly into the <strong>Cashier Live Order Queue</strong> with chime notifications.
            </div>
          </div>
          <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;">
            <a href="https://easy-scan.shop/r/${escapeHtml(menuSlug)}" target="_blank" rel="noopener noreferrer" class="ess-btn ess-btn-secondary" style="font-weight:700;display:inline-flex;align-items:center;gap:6px;text-decoration:none;">
              Open QR Menu &nearr;
            </a>
            <button type="button" class="ess-btn ess-btn-primary" id="own-card-sync-easymenu" style="background:var(--ess-orange-primary);color:#fff;font-weight:700;box-shadow:0 3px 10px rgba(249,115,22,0.3);">
              ⚡ Sync Dishes &amp; Photos
            </button>
            <button type="button" class="ess-btn ess-btn-secondary" id="own-card-test-qr-order" style="font-weight:700;border-color:var(--ess-primary);color:var(--ess-primary);">
              🛎️ Test QR Order to Cashier
            </button>
          </div>
        </div>

        <!-- EasyMenu Pairing & Secret Key Box -->
        <div style="margin-top:16px;padding:14px;background:var(--ess-bg-card,#fff);border:1px solid var(--ess-border-color);border-radius:8px;">
          <div style="font-size:0.82rem;font-weight:700;color:var(--ess-text-primary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
            <span>🔑 EasyMenu &harr; POS Synchronization &amp; Secret Key</span>
          </div>
          <div class="ess-grid-2" style="gap:12px;">
            <div>
              <label class="ess-label" style="font-size:0.75rem;margin-bottom:3px;">Restaurant Secret Key (Copy to EasyMenu settings):</label>
              <div style="display:flex;align-items:center;gap:6px;">
                <input type="text" readonly id="own-secret-key-val" class="ess-input" value="${escapeHtml(secretKey)}" style="font-family:monospace;font-size:0.85rem;background:var(--ess-bg-hover);cursor:pointer;" />
                <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="own-btn-copy-secret" style="white-space:nowrap;font-weight:700;">
                  📋 Copy Key
                </button>
              </div>
            </div>
            <div>
              <label class="ess-label" style="font-size:0.75rem;margin-bottom:3px;">POS Webhook Ingestion URL (easy-scan.shop &rarr; POS):</label>
              <div style="display:flex;align-items:center;gap:6px;">
                <input type="text" readonly id="own-webhook-url-val" class="ess-input" value="https://pos.easy-scan.shop/api/v1/qr-orders/submit" style="font-family:monospace;font-size:0.85rem;background:var(--ess-bg-hover);cursor:pointer;" />
                <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="own-btn-copy-webhook" style="white-space:nowrap;font-weight:700;">
                  📋 Copy URL
                </button>
              </div>
            </div>
          </div>
          <div style="font-size:0.75rem;color:var(--ess-text-muted);margin-top:8px;">
            💡 <em>Copy this Secret Key and paste it into EasyMenu on <code>easy-scan.shop</code> to securely synchronize dishes and receive live customer table orders.</em>
          </div>
        </div>
      </div>

      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card ess-kpi-highlight">
          <div class="ess-kpi-label">Today's Gross Revenue</div>
          <div class="ess-kpi-value">${formatCurrency(params.dailySales?.totalRevenueCents ?? 0)}</div>
          <div class="ess-kpi-meta">Gross Profit: ${formatCurrency(params.dailySales?.grossProfitCents ?? 0)} (${params.dailySales?.profitMarginPct ?? 0}%)</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Inventory Retail Valuation</div>
          <div class="ess-kpi-value">${formatCurrency(params.valuation?.totalRetailValueCents ?? 0)}</div>
          <div class="ess-kpi-meta">Cost Value: ${formatCurrency(params.valuation?.totalCostValueCents ?? 0)}</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Active Branches &amp; Staff</div>
          <div class="ess-kpi-value">${params.branches.length} Branches / ${params.staff.length} Staff</div>
          <div class="ess-kpi-meta">Within ${escapeHtml(plan?.name || 'Plan')} quota</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">SaaS Subscription Status</div>
          <div class="ess-kpi-value">${escapeHtml(params.subStatus?.subscriptionStatus || 'ACTIVE')}</div>
          <div class="ess-kpi-meta">${params.subStatus?.daysRemaining ?? 30} days remaining in billing period</div>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Multi-Branch Operations Summary</h3>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="own-go-branches">Manage Branches</button>
          </div>
          <div class="ess-stack-sm">
            ${params.branches
              .map(
                (b) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(b.name)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">Branch Code: ${escapeHtml(b.code)} &bull; Phone: ${escapeHtml(b.phone || '—')}</div>
                </div>
                <span class="ess-badge ess-badge-success">ACTIVE</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Tenant Data Isolation &amp; Financial Guarantees</h3>
            <span class="ess-badge ess-badge-success">Protected</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row"><span>PostgreSQL Row-Level Security (RLS)</span><strong>Strict Tenant Session</strong></div>
            <div class="ess-kv-row"><span>Historical Receipt Snapshots</span><strong>Immutable Pricing</strong></div>
            <div class="ess-kv-row"><span>Media &amp; Menu Photos</span><strong>S3 / Cloudflare R2 Storage</strong></div>
            <div class="ess-kv-row"><span>Autonomous Subscription Unlocking</span><strong>HMAC-SHA256 M-Pesa Webhooks</strong></div>
          </div>
        </div>
      </div>
    </div>
  `;

  const openSyncModal = () => {
    openModal({
      title: 'Import Menu & Photos from EasyMenu',
      subtitle: `Synchronize categories, dishes, prices, and food photos from easy-scan.shop`,
      width: 'md',
      bodyHtml: `
        <div class="ess-stack-md">
          <p style="font-size:0.85rem;color:var(--ess-text-secondary);margin:0;line-height:1.45;">
            Synchronize all menu categories, crafted beverages, prime dishes, prices, and high-resolution food photography from EasyMenu (<code>easy-scan.shop</code>) directly into this POS counter catalog.
          </p>
          <div class="ess-form-group">
            <label class="ess-label">EasyMenu Digital Menu Slug *</label>
            <div style="display:flex;align-items:center;gap:4px;">
              <span style="color:var(--ess-text-muted);font-size:0.85rem;background:var(--ess-bg-hover);padding:0.5rem;border:1px solid var(--ess-border-color);border-radius:6px 0 0 6px;">https://easy-scan.shop/r/</span>
              <input type="text" id="sync-slug-input" class="ess-input" value="${escapeHtml(menuSlug)}" placeholder="e.g. sip-chat-cafe" style="border-radius:0 6px 6px 0;" />
            </div>
            <div style="font-size:0.75rem;color:var(--ess-text-muted);margin-top:4px;">Dishes and categories will be created and linked to this restaurant.</div>
          </div>
          <div class="ess-form-group" style="margin-top:8px;">
            <label class="ess-label">Restaurant Secret Key (for EasyMenu pairing)</label>
            <div style="display:flex;align-items:center;gap:6px;">
              <input type="text" readonly class="ess-input" value="${escapeHtml(secretKey)}" style="font-family:monospace;font-size:0.85rem;background:var(--ess-bg-hover);" />
              <button type="button" class="ess-btn ess-btn-secondary" id="sync-modal-copy-key" style="white-space:nowrap;font-weight:700;">📋 Copy</button>
            </div>
          </div>
          <div id="sync-modal-error" style="display:none;" class="ess-alert"></div>
        </div>
      `,
      footerHtml: `
        <div style="display:flex;justify-content:flex-end;gap:0.5rem;width:100%;">
          <button type="button" class="ess-btn ess-btn-secondary" id="sync-cancel-btn">Cancel</button>
          <button type="button" class="ess-btn ess-btn-primary" id="sync-confirm-btn" style="background:var(--ess-orange-primary);color:#fff;font-weight:700;">
            ⚡ Start Import &amp; Sync
          </button>
        </div>
      `,
      onMount: (modalRoot, close) => {
        const cancelBtn = modalRoot.querySelector('#sync-cancel-btn') as HTMLButtonElement;
        const confirmBtn = modalRoot.querySelector('#sync-confirm-btn') as HTMLButtonElement;
        const slugInput = modalRoot.querySelector('#sync-slug-input') as HTMLInputElement;
        const errorBox = modalRoot.querySelector('#sync-modal-error') as HTMLElement;

        cancelBtn?.addEventListener('click', close);

        modalRoot.querySelector('#sync-modal-copy-key')?.addEventListener('click', () => {
          navigator.clipboard.writeText(secretKey).then(() => {
            showToast({ title: 'Secret Key Copied', message: 'Secret key copied to clipboard', type: 'success' });
          });
        });

        confirmBtn?.addEventListener('click', async () => {
          const slug = slugInput.value.trim().toLowerCase() || menuSlug;
          confirmBtn.disabled = true;
          confirmBtn.innerHTML = '<span class="ess-spinner-sm" style="display:inline-block;margin-right:6px;"></span> Synchronizing Dishes &amp; Photos...';
          if (errorBox) errorBox.style.display = 'none';

          try {
            const res = await apiClient.importEasyMenu(slug);
            showToast({
              title: 'EasyMenu Synchronized',
              message: res.message || 'Menu items and photos loaded into POS.',
              type: 'success',
            });
            close();
            if (params.onRefresh) params.onRefresh();
          } catch (err: any) {
            confirmBtn.disabled = false;
            confirmBtn.innerHTML = '⚡ Start Import &amp; Sync';
            if (errorBox) {
              errorBox.className = 'ess-alert ess-alert-danger';
              errorBox.textContent = err?.message || 'Could not import from EasyMenu.';
              errorBox.style.display = 'block';
            }
          }
        });
      },
    });
  };

  viewport.querySelector('#own-top-sync-easymenu')?.addEventListener('click', openSyncModal);
  viewport.querySelector('#own-card-sync-easymenu')?.addEventListener('click', openSyncModal);

  viewport.querySelector('#own-btn-copy-secret')?.addEventListener('click', () => {
    navigator.clipboard.writeText(secretKey).then(() => {
      showToast({ title: 'Secret Key Copied', message: 'Copied secret key to clipboard', type: 'success' });
    });
  });

  viewport.querySelector('#own-btn-copy-webhook')?.addEventListener('click', () => {
    navigator.clipboard.writeText('https://pos.easy-scan.shop/api/v1/qr-orders/submit').then(() => {
      showToast({ title: 'Webhook URL Copied', message: 'Copied webhook ingestion URL to clipboard', type: 'success' });
    });
  });

  // Test Live QR Table Order to Cashier
  viewport.querySelector('#own-card-test-qr-order')?.addEventListener('click', async () => {
    try {
      const products = await apiClient.listProducts();
      if (!products || products.length === 0) {
        showToast({ title: 'No Dishes Found', message: 'Please sync dishes from EasyMenu first.', type: 'warning' });
        return;
      }
      const sampleItem = products[0];
      const variant = sampleItem.variants?.[0];
      if (!variant) return;

      const res = await apiClient.submitQrOrder({
        tenantId: tenant?.id || apiClient.getEffectiveTenantId(),
        tableNumber: 'Table 5',
        source: 'EASYMENU',
        customerName: 'Online Diner (EasyMenu)',
        customerPhone: '254712345678',
        customerNotes: 'Extra hot with vanilla shot please',
        items: [{ variantId: variant.id, quantity: 2, notes: 'Direct from QR menu' }],
      });

      showToast({
        title: '🛎️ QR Order Placed at Table 5!',
        message: `Order #${res.data?.orderNumber || '101'} placed! Check Cashier incoming queue.`,
        type: 'success',
      });

      openModal({
        title: '🛎️ QR Table Order Sent to Cashier Queue!',
        subtitle: 'Live simulated diner submission from easy-scan.shop QR menu',
        width: 'sm',
        bodyHtml: `
          <div class="ess-stack-md" style="text-align:center;padding:12px 0;">
            <div style="font-size:42px;">🛎️</div>
            <div style="font-size:16px;font-weight:800;color:var(--ess-text-primary);">Order Successfully Placed!</div>
            <p style="font-size:13px;color:var(--ess-text-secondary);margin:0;line-height:1.5;">
              Diner at <strong>Table 5</strong> placed an order for <strong>2x ${escapeHtml(sampleItem.name)}</strong>.<br><br>
              The Cashier POS station has received an active yellow alert and incoming order badge to review, confirm, and route to the kitchen!
            </p>
          </div>
        `,
        footerHtml: `
          <div style="display:flex;justify-content:center;gap:10px;width:100%;">
            <button type="button" class="ess-btn ess-btn-secondary" id="em-test-close">Close</button>
            <button type="button" class="ess-btn ess-btn-primary" id="em-go-cashier" style="background:var(--ess-orange-primary);color:#fff;font-weight:700;">
              Switch to Cashier POS &rarr;
            </button>
          </div>
        `,
        onMount: (modalRoot, close) => {
          modalRoot.querySelector('#em-test-close')?.addEventListener('click', close);
          modalRoot.querySelector('#em-go-cashier')?.addEventListener('click', () => {
            close();
            apiClient.setActiveWorkspace('CASHIER');
            window.location.reload();
          });
        },
      });
    } catch (err: any) {
      showToast({ title: 'Order Submission Failed', message: err?.message, type: 'danger' });
    }
  });

  viewport.querySelector('#own-go-menu')?.addEventListener('click', () => params.onNavigateTab('menu-config'));
  viewport.querySelector('#own-go-sub')?.addEventListener('click', () => params.onNavigateTab('subscription'));
  viewport.querySelector('#own-go-branches')?.addEventListener('click', () => params.onNavigateTab('branches-staff'));
}

function renderOwnerSubscriptionScreen(
  viewport: HTMLElement,
  params: {
    subStatus: any;
    plans: any[];
    invoices: any[];
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Subscription Plan &amp; Autonomous M-Pesa Billing</h2>
          <p class="ess-section-desc">Initiate M-Pesa STK push renewals and view verified SaaS invoices</p>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Current Subscription Status</h3>
            <span class="ess-badge ${
              params.subStatus?.tenantStatus === 'ACTIVE' ? 'ess-badge-success' : 'ess-badge-warning'
            }">${escapeHtml(params.subStatus?.tenantStatus || 'ACTIVE')}</span>
          </div>
          <div class="ess-stack-sm" style="margin-bottom:16px;">
            <div class="ess-kv-row"><span>Active Plan</span><strong>${escapeHtml(params.subStatus?.plan?.name)} (${escapeHtml(params.subStatus?.plan?.code)})</strong></div>
            <div class="ess-kv-row"><span>Monthly Rate</span><strong>${formatCurrency(params.subStatus?.plan?.monthlyPriceCents)}</strong></div>
            <div class="ess-kv-row"><span>Current Period Ends</span><strong>${formatDateTime(params.subStatus?.currentPeriodEnd)}</strong></div>
            <div class="ess-kv-row"><span>Days Remaining</span><strong>${params.subStatus?.daysRemaining ?? 30} Days</strong></div>
          </div>

          <form id="mpesa-renew-form" class="ess-form" style="border-top:1px solid var(--ess-border);padding-top:14px;">
            <div class="ess-form-group">
              <label class="ess-label">M-Pesa Handset Number for STK Push Renewal *</label>
              <input type="tel" id="sub-mpesa-phone" class="ess-input" value="254711000001" placeholder="2547XXXXXXXX" required />
            </div>
            <button type="submit" class="ess-btn ess-btn-primary ess-btn-block" id="sub-mpesa-btn">
              Pay / Renew Subscription via M-Pesa STK Push
            </button>
          </form>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Subscription Invoices</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.invoices
              .map(
                (inv) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(inv.invoice_number)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">Due: ${formatDateTime(inv.due_date)}</div>
                </div>
                <div style="text-align:right;">
                  <strong>${formatCurrency(inv.amount_cents)}</strong>
                  <div><span class="ess-badge ${inv.status === 'PAID' ? 'ess-badge-success' : 'ess-badge-warning'}">${escapeHtml(inv.status)}</span></div>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>

      <div class="ess-card">
        <div class="ess-card-header">
          <h3 class="ess-card-title">Available ESS Subscription Tiers</h3>
        </div>
        <div class="ess-grid-3">
          ${params.plans
            .map(
              (p) => `
            <div style="border:1px solid ${
              p.code === params.subStatus?.plan?.code ? 'var(--ess-primary)' : 'var(--ess-border)'
            };border-radius:8px;padding:16px;background:${
              p.code === params.subStatus?.plan?.code ? 'var(--ess-primary-soft)' : '#fff'
            };">
              <div style="display:flex;justify-content:space-between;align-items:center;">
                <strong>${escapeHtml(p.name)}</strong>
                ${
                  p.code === params.subStatus?.plan?.code
                    ? '<span class="ess-badge ess-badge-orange">Current Plan</span>'
                    : ''
                }
              </div>
              <div style="font-size:20px;font-weight:700;margin:8px 0;">${formatCurrency(p.monthly_price_cents)}<span style="font-size:12px;font-weight:400;">/mo</span></div>
              <div style="font-size:12px;color:var(--ess-text-secondary);">Up to ${p.max_branches} branches &bull; ${p.max_users} staff accounts</div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#mpesa-renew-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const phone = (viewport.querySelector('#sub-mpesa-phone') as HTMLInputElement).value.trim();
    const btn = viewport.querySelector('#sub-mpesa-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Dispatching M-Pesa STK Push...';

    try {
      const res = await apiClient.initiateSubscriptionMpesaPayment({ phoneNumber: phone });
      showToast({
        title: 'M-Pesa STK Prompt Dispatched',
        message: `${res.message} (Checkout ID: ${res.checkoutId})`,
        type: 'info',
      });
      params.onRefresh();
    } catch (err: any) {
      showToast({
        title: 'Payment Dispatch Failed',
        message: err?.message || 'Unable to initiate STK push',
        type: 'danger',
      });
    } finally {
      btn.disabled = false;
      btn.textContent = 'Pay / Renew Subscription via M-Pesa STK Push';
    }
  });
}

function renderOwnerMenuConfigScreen(
  viewport: HTMLElement,
  params: {
    categories: any[];
    products: any[];
    locations: any[];
    onRefresh: () => void;
  }
) {
  let uploadedImageUrl = '';

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Menu, Category &amp; Product Photo Configuration</h2>
          <p class="ess-section-desc">Configure restaurant dishes, pricing, categories, and S3/R2 object-storage photos</p>
        </div>
        <div class="ess-section-header-actions">
          <button type="button" class="ess-btn ess-btn-secondary" id="owner-sync-easymenu-btn">
            ⚡ One-Click Sync from EasyMenu
          </button>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Create New Menu Item &amp; Upload Photo</h3>
          </div>
          <form id="owner-create-product-form" class="ess-form">
            <div class="ess-grid-2">
              <div class="ess-form-group">
                <label class="ess-label">Dish / Product Name *</label>
                <input type="text" id="om-name" class="ess-input" placeholder="e.g. Grilled Red Snapper Fillet" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">SKU Code *</label>
                <input type="text" id="om-sku" class="ess-input" placeholder="e.g. SEA-004" required />
              </div>
            </div>

            <div class="ess-grid-2">
              <div class="ess-form-group">
                <label class="ess-label">Menu Category</label>
                <select id="om-cat" class="ess-select">
                  ${params.categories.map((c) => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`).join('')}
                </select>
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Stock Location</label>
                <select id="om-loc" class="ess-select">
                  ${params.locations.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('')}
                </select>
              </div>
            </div>

            <div class="ess-grid-3">
              <div class="ess-form-group">
                <label class="ess-label">Cost Price (KES) *</label>
                <input type="number" id="om-cost" class="ess-input" min="0" value="650" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Menu Price (KES) *</label>
                <input type="number" id="om-price" class="ess-input" min="0" value="1450" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Initial Stock *</label>
                <input type="number" id="om-stock" class="ess-input" min="0" value="15" required />
              </div>
            </div>

            ${renderImageUploadControl('owner-menu-photo', 'Dish Photo (Camera or Gallery -> S3 Object Storage)')}

            <button type="submit" class="ess-btn ess-btn-primary ess-btn-block">
              Publish Menu Dish to POS &amp; Waiter Catalog
            </button>
          </form>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Menu Categories (${params.categories.length})</h3>
          </div>
          <form id="owner-create-cat-form" class="ess-form" style="margin-bottom:16px;">
            <div style="display:flex;gap:8px;">
              <input type="text" id="om-new-cat-name" class="ess-input" placeholder="New Category Name (e.g. Chef Specials)..." required />
              <button type="submit" class="ess-btn ess-btn-secondary">+ Add Category</button>
            </div>
          </form>
          <div class="ess-stack-sm">
            ${params.categories
              .map(
                (c) => `
              <div class="ess-kv-row">
                <strong>${escapeHtml(c.name)}</strong>
                <span class="ess-badge ess-badge-neutral">Active</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  bindImageUploadControl(viewport, 'owner-menu-photo', (mediaUrl) => {
    uploadedImageUrl = mediaUrl;
  });

  viewport.querySelector('#owner-sync-easymenu-btn')?.addEventListener('click', () => {
    openModal({
      title: 'Import Menu & Food Photos from EasyMenu',
      subtitle: 'Synchronize categories, dishes, prices, and high-res photography from easy-scan.shop',
      width: 'md',
      bodyHtml: `
        <div class="ess-stack-md">
          <p style="font-size:0.85rem;color:var(--ess-text-secondary);margin:0;line-height:1.45;">
            Synchronize all menu categories, crafted beverages, prime dishes, prices, and high-resolution food photography from EasyMenu (<code>easy-scan.shop</code>) directly into this POS counter catalog.
          </p>
          <div class="ess-form-group">
            <label class="ess-label">EasyMenu Digital Menu Slug *</label>
            <div style="display:flex;align-items:center;gap:4px;">
              <span style="color:var(--ess-text-muted);font-size:0.85rem;background:var(--ess-bg-hover);padding:0.5rem;border:1px solid var(--ess-border-color);border-radius:6px 0 0 6px;">https://easy-scan.shop/r/</span>
              <input type="text" id="sync-slug-input" class="ess-input" value="sip-chat-cafe" placeholder="e.g. sip-chat-cafe" style="border-radius:0 6px 6px 0;" />
            </div>
            <div style="font-size:0.75rem;color:var(--ess-text-muted);margin-top:4px;">Dishes and categories will be created and linked to this restaurant.</div>
          </div>
          <div id="sync-modal-error" style="display:none;" class="ess-alert"></div>
        </div>
      `,
      footerHtml: `
        <div style="display:flex;justify-content:flex-end;gap:0.5rem;width:100%;">
          <button type="button" class="ess-btn ess-btn-secondary" id="sync-cancel-btn">Cancel</button>
          <button type="button" class="ess-btn ess-btn-primary" id="sync-confirm-btn" style="background:var(--ess-orange-primary);color:#fff;font-weight:700;">
            ⚡ Start Import &amp; Sync
          </button>
        </div>
      `,
      onMount: (modalRoot, close) => {
        const cancelBtn = modalRoot.querySelector('#sync-cancel-btn') as HTMLButtonElement;
        const confirmBtn = modalRoot.querySelector('#sync-confirm-btn') as HTMLButtonElement;
        const slugInput = modalRoot.querySelector('#sync-slug-input') as HTMLInputElement;
        const errorBox = modalRoot.querySelector('#sync-modal-error') as HTMLElement;

        cancelBtn?.addEventListener('click', close);

        modalRoot.querySelector('#sync-modal-copy-key')?.addEventListener('click', () => {
          navigator.clipboard.writeText(secretKey).then(() => {
            showToast({ title: 'Secret Key Copied', message: 'Secret key copied to clipboard', type: 'success' });
          });
        });

        confirmBtn?.addEventListener('click', async () => {
          const slug = slugInput.value.trim().toLowerCase() || 'sip-chat-cafe';
          confirmBtn.disabled = true;
          confirmBtn.innerHTML = '<span class="ess-spinner-sm" style="display:inline-block;margin-right:6px;"></span> Synchronizing Dishes &amp; Photos...';
          if (errorBox) errorBox.style.display = 'none';

          try {
            const res = await apiClient.importEasyMenu(slug);
            showToast({
              title: 'EasyMenu Synchronized',
              message: res.message || 'Menu items and photos loaded into POS.',
              type: 'success',
            });
            close();
            params.onRefresh();
          } catch (err: any) {
            confirmBtn.disabled = false;
            confirmBtn.innerHTML = '⚡ Start Import &amp; Sync';
            if (errorBox) {
              errorBox.className = 'ess-alert ess-alert-danger';
              errorBox.textContent = err?.message || 'Could not import from EasyMenu.';
              errorBox.style.display = 'block';
            }
          }
        });
      },
    });
  });

  viewport.querySelector('#owner-create-cat-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (viewport.querySelector('#om-new-cat-name') as HTMLInputElement).value.trim();
    if (!name) return;
    try {
      await apiClient.createCategory(name);
      showToast({ title: 'Category Created', message: name, type: 'success' });
      params.onRefresh();
    } catch (err: any) {
      showToast({ title: 'Failed', message: err?.message, type: 'danger' });
    }
  });

  viewport.querySelector('#owner-create-product-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (viewport.querySelector('#om-name') as HTMLInputElement).value.trim();
    const sku = (viewport.querySelector('#om-sku') as HTMLInputElement).value.trim();
    const categoryId = (viewport.querySelector('#om-cat') as HTMLSelectElement).value;
    const stockLocationId = (viewport.querySelector('#om-loc') as HTMLSelectElement).value;
    const costKes = Number((viewport.querySelector('#om-cost') as HTMLInputElement).value || 0);
    const priceKes = Number((viewport.querySelector('#om-price') as HTMLInputElement).value || 0);
    const initialStock = Number((viewport.querySelector('#om-stock') as HTMLInputElement).value || 0);

    try {
      await apiClient.createProduct({
        name,
        description: uploadedImageUrl ? `Media Ref: ${uploadedImageUrl}` : undefined,
        categoryId: categoryId || undefined,
        variants: [
          {
            sku,
            variantName: 'Standard Portion',
            costPriceCents: Math.round(costKes * 100),
            retailPriceCents: Math.round(priceKes * 100),
            initialStock,
            stockLocationId,
          },
        ],
      });
      showToast({
        title: 'Menu Dish Published',
        message: `${name} (${sku}) is now live in Cashier & Waiter workspaces.`,
        type: 'success',
      });
      params.onRefresh();
    } catch (err: any) {
      showToast({ title: 'Could Not Create Product', message: err?.message, type: 'danger' });
    }
  });
}

function renderOwnerBranchesAndStaffScreen(
  viewport: HTMLElement,
  params: {
    profile: any;
    branches: any[];
    locations: any[];
    staff: any[];
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Branches, Stock Locations &amp; Employee Role Assignments</h2>
          <p class="ess-section-desc">Expand restaurant branches and manage employee RBAC roles</p>
        </div>
        <div class="ess-btn-group">
          <button type="button" class="ess-btn ess-btn-secondary" id="own-add-branch-btn">+ New Branch</button>
          <button type="button" class="ess-btn ess-btn-primary" id="own-add-staff-btn">+ Add Employee</button>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Restaurant Branches (${params.branches.length})</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.branches
              .map(
                (b) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(b.name)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">Code: ${escapeHtml(b.code)} &bull; ${escapeHtml(b.address || 'Main Location')}</div>
                </div>
                <span class="ess-badge ess-badge-success">ACTIVE</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Assigned Restaurant Staff (${params.staff.length})</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.staff
              .map(
                (u) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(u.firstName)} ${escapeHtml(u.lastName)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">${escapeHtml(u.email)}</div>
                </div>
                <div>
                  ${(u.roles || [])
                    .map((r: any) => `<span class="ess-badge ess-badge-orange" style="margin-left:4px;">${escapeHtml(r.role)}</span>`)
                    .join('')}
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#own-add-staff-btn')?.addEventListener('click', () => {
    openAddStaffModal(params.branches, params.onRefresh);
  });

  viewport.querySelector('#own-add-branch-btn')?.addEventListener('click', () => {
    openModal({
      title: 'Create New Restaurant Branch',
      subtitle: 'Automatically provisions a default Retail Stock Location for the branch',
      width: 'sm',
      bodyHtml: `
        <div class="ess-form">
          <div class="ess-form-group"><label class="ess-label">Branch Name *</label><input type="text" id="br-name" class="ess-input" placeholder="e.g. Westlands Rooftop Branch" required /></div>
          <div class="ess-form-group"><label class="ess-label">Branch Code *</label><input type="text" id="br-code" class="ess-input" placeholder="e.g. WST" required /></div>
          <div class="ess-form-group"><label class="ess-label">Address</label><input type="text" id="br-addr" class="ess-input" placeholder="Westlands Road, Nairobi" /></div>
        </div>
      `,
      footerHtml: `
        <button type="button" class="ess-btn ess-btn-secondary" id="br-cancel">Cancel</button>
        <button type="button" class="ess-btn ess-btn-primary" id="br-save">Create Branch</button>
      `,
      onMount: (root, close) => {
        root.querySelector('#br-cancel')?.addEventListener('click', close);
        root.querySelector('#br-save')?.addEventListener('click', async () => {
          const name = (root.querySelector('#br-name') as HTMLInputElement).value.trim();
          const code = (root.querySelector('#br-code') as HTMLInputElement).value.trim();
          const address = (root.querySelector('#br-addr') as HTMLInputElement).value.trim();
          if (!name || !code) return;
          try {
            await apiClient.createBranch({ name, code, address: address || undefined });
            close();
            showToast({ title: 'Branch Created', message: `${name} (${code})`, type: 'success' });
            params.onRefresh();
          } catch (err: any) {
            showToast({ title: 'Branch Creation Failed', message: err?.message, type: 'danger' });
          }
        });
      },
    });
  });
}

function renderOwnerSettingsAndAuditScreen(
  viewport: HTMLElement,
  params: {
    profile: any;
    auditLogs: any[];
  }
) {
  const tenant = params.profile?.tenant;
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Business Settings, Payment Configuration &amp; Audit Logs</h2>
          <p class="ess-section-desc">Configured payment channels and immutable tenant security logs</p>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Payment &amp; Business Configuration</h3>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row"><span>Business Legal Name</span><strong>${escapeHtml(tenant?.name)}</strong></div>
            <div class="ess-kv-row"><span>Tenant Identifier</span><strong>${escapeHtml(tenant?.slug)}</strong></div>
            <div class="ess-kv-row"><span>Base Currency</span><strong>${escapeHtml(tenant?.currency || 'KES')}</strong></div>
            <div class="ess-kv-row"><span>Primary M-Pesa Gateway</span><span class="ess-badge ess-badge-success">IntaSend / Safaricom Daraja</span></div>
            <div class="ess-kv-row"><span>Webhook Verification</span><strong>HMAC-SHA256 Enforced</strong></div>
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Tenant Security &amp; Operational Audit Trail</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.auditLogs
              .map(
                (l) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(l.action)}</strong> &bull; ${escapeHtml(l.actor)}
                  <div style="font-size:12px;color:var(--ess-text-muted);">${escapeHtml(l.target)}</div>
                </div>
                <span style="font-size:11px;color:var(--ess-text-muted);">${formatDateTime(l.timestamp)}</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================================================
// PART B: ESS SUPER ADMIN PLATFORM WORKSPACE (Strictly Isolated Platform Level)
// ============================================================================

export async function renderSuperAdminWorkspace(
  viewport: HTMLElement,
  activeTab: string,
  onNavigateTab: (tabId: string) => void
) {
  const user = apiClient.getUser();
  if (!user?.isSuperAdmin) {
    viewport.innerHTML = renderErrorState({
      title: 'Platform Access Restricted',
      message: 'This workspace is strictly isolated for ESS Platform Super Administrators.',
    });
    return;
  }

  viewport.innerHTML = renderLoadingState('Loading ESS Platform Super Admin Console...');

  try {
    const [overview, health] = await Promise.all([
      apiClient.getPlatformOverview(),
      apiClient.getSystemHealth(),
    ]);

    const refresh = () => renderSuperAdminWorkspace(viewport, activeTab, onNavigateTab);

    if (activeTab === 'plans-billing') {
      renderSuperAdminPlansScreen(viewport, {
        overview,
        onRefresh: refresh,
      });
    } else if (activeTab === 'platform-health') {
      renderSuperAdminHealthScreen(viewport, {
        health,
        overview,
      });
    } else if (activeTab === 'support-audit') {
      renderSuperAdminSupportScreen(viewport, {
        overview,
      });
    } else if (activeTab === 'add-restaurant') {
      renderSuperAdminAddRestaurantScreen(viewport, {
        onSuccess: () => onNavigateTab('tenants'),
        onCancel: () => onNavigateTab('tenants'),
      });
    } else {
      // Default: 'tenants'
      renderSuperAdminTenantsScreen(viewport, {
        overview,
        onRefresh: refresh,
      });
    }
  } catch (err: any) {
    viewport.innerHTML = renderErrorState({
      title: 'Failed to Load ESS Super Admin Console',
      message: err?.message || 'Unable to load platform overview.',
      retryButtonId: 'sa-retry-btn',
    });
    viewport.querySelector('#sa-retry-btn')?.addEventListener('click', () => {
      renderSuperAdminWorkspace(viewport, activeTab, onNavigateTab);
    });
  }
}

function renderSuperAdminTenantsScreen(
  viewport: HTMLElement,
  params: {
    overview: any;
    onRefresh: () => void;
  }
) {
  const m = params.overview.metrics;
  const allTenants = params.overview.tenants || [];
  let filterText = '';
  let filterStatus = 'ALL';
  let filterPlan = 'ALL';

  function renderTableRows(tenants: any[]) {
    if (tenants.length === 0) {
      return `
        <tr>
          <td colspan="7" style="text-align:center; padding: 2rem;">
            <div style="color:var(--ess-text-secondary); margin-bottom: 0.5rem;">No restaurant tenants found matching your filter criteria.</div>
            <button type="button" class="ess-btn ess-btn-primary ess-btn-sm" id="btn-onboard-empty">
              + Onboard First Restaurant
            </button>
          </td>
        </tr>
      `;
    }

    return tenants
      .map(
        (t: any) => `
      <tr data-tenant-row="${escapeHtml(t.id)}">
        <td>
          <div style="font-weight:600; font-size:0.95rem; color:var(--ess-text-primary);">${escapeHtml(t.name)}</div>
          <div style="font-size:0.75rem; color:var(--ess-text-secondary); margin-top:2px;">
            Currency: <strong>${escapeHtml(t.currency || 'KES')}</strong>
            ${
              t.easy_menu_slug
                ? `&bull; <a href="https://easy-scan.shop/r/${escapeHtml(t.easy_menu_slug)}" target="_blank" rel="noopener noreferrer" style="color:var(--ess-orange-primary); text-decoration:none;">QR Menu &nearr;</a>`
                : ''
            }
          </div>
        </td>
        <td>
          <code style="background:var(--ess-bg-hover); padding:2px 6px; border-radius:4px; font-size:0.8rem;">${escapeHtml(t.slug)}</code>
        </td>
        <td>
          <span class="ess-badge ess-badge-neutral">${escapeHtml(t.planName)}</span>
          <div style="font-size:0.8rem; font-weight:600; margin-top:3px; color:var(--ess-text-primary);">${formatCurrency(t.monthlyPriceCents)}/mo</div>
        </td>
        <td>
          <span class="ess-badge ess-badge-orange" style="font-weight:700;">${t.tokensRemaining ?? 30} Tokens</span>
          <div style="font-size:0.75rem; color:var(--ess-text-secondary); margin-top:2px;">${t.hoursRemaining ?? 720} hrs (${Math.round((t.hoursRemaining ?? 720) / 24)}d) &bull; 1 token = 24h</div>
        </td>
        <td>
          <div style="font-weight:500;">${t.branchesCount} Branches</div>
          <div style="font-size:0.75rem; color:var(--ess-text-secondary);">${t.usersCount} Staff Accounts</div>
        </td>
        <td>
          <span class="ess-badge ${
            t.status === 'ACTIVE'
              ? 'ess-badge-success'
              : t.status === 'GRACE_PERIOD'
              ? 'ess-badge-warning'
              : 'ess-badge-danger'
          }">${escapeHtml(t.status)}</span>
        </td>
        <td>
          <div class="ess-btn-group" style="flex-wrap:nowrap;">
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-easymenu="${escapeHtml(t.id)}" data-sa-tenant-name="${escapeHtml(t.name)}" data-sa-tenant-slug="${escapeHtml(t.slug)}" data-sa-easymenu-slug="${escapeHtml(t.easy_menu_slug || t.slug)}" data-sa-secret-key="${escapeHtml(t.secret_key || t.secretKey || '')}" style="font-weight:700;color:var(--ess-orange-primary);border-color:var(--ess-orange-primary);background:rgba(249,115,22,0.06);" title="EasyMenu 2-Way Connection & 1-Click Sync">⚡ EasyMenu</button>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-manage-menu="${escapeHtml(t.id)}" style="font-weight:700;color:var(--ess-primary);" title="Manage Dishes in Owner Suite">🍽️ Dishes</button>
            <button type="button" class="ess-btn ess-btn-primary ess-btn-sm" data-sa-grant-tokens="${escapeHtml(t.id)}" data-sa-tenant-name="${escapeHtml(t.name)}" data-sa-current-tokens="${t.tokensRemaining ?? 30}" style="background:var(--ess-orange-primary); color:#fff;" title="Grant or Top-Up Tokens (+24h each)">+ Tokens</button>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-add-branch="${escapeHtml(t.id)}" data-sa-tenant-name="${escapeHtml(t.name)}" title="Assign Additional Branch">+ Branch</button>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-add-staff="${escapeHtml(t.id)}" data-sa-tenant-name="${escapeHtml(t.name)}" title="Assign Staff Member">+ Staff</button>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-switch-tenant="${escapeHtml(t.id)}" title="Switch to Tenant Operational Context">Context</button>
            ${
              t.status === 'ACTIVE'
                ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-tenant="${escapeHtml(t.id)}" data-sa-status="LOCKED_UNPAID" title="Lock Tenant Account">Lock</button>`
                : `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-sa-tenant="${escapeHtml(t.id)}" data-sa-status="ACTIVE" title="Activate Tenant Account">Activate</button>`
            }
            <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" style="color:var(--ess-danger-text);" data-sa-delete-tenant="${escapeHtml(t.id)}" data-sa-tenant-name="${escapeHtml(t.name)}" title="Delete Restaurant Tenant">&times;</button>
          </div>
        </td>
      </tr>
    `
      )
      .join('');
  }

  function getFilteredTenants() {
    return allTenants.filter((t: any) => {
      const matchText =
        !filterText ||
        t.name.toLowerCase().includes(filterText) ||
        t.slug.toLowerCase().includes(filterText);
      const matchStatus = filterStatus === 'ALL' || t.status === filterStatus;
      const matchPlan = filterPlan === 'ALL' || t.planCode === filterPlan;
      return matchText && matchStatus && matchPlan;
    });
  }

  function renderView() {
    viewport.innerHTML = `
      <div class="ess-stack-lg">
        <div class="ess-section-header" style="display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:1rem;">
          <div>
            <h2 class="ess-section-title">ESS Super Admin &mdash; Multi-Tenant Restaurant Governance &amp; Onboarding</h2>
            <p class="ess-section-desc">Platform administration: Assign, configure, and govern restaurant tenants across Kenya &amp; East Africa</p>
          </div>
          <div style="display:flex; gap:0.5rem; align-items:center;">
            <button type="button" class="ess-btn ess-btn-secondary" id="btn-refresh-tenants">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:4px;"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              Refresh
            </button>
            <button type="button" class="ess-btn ess-btn-primary" id="btn-onboard-restaurant" style="background:var(--ess-orange-primary); color:#fff; font-weight:600;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:4px;"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              + Onboard New Restaurant
            </button>
          </div>
        </div>

        <div class="ess-kpi-grid">
          <div class="ess-card ess-kpi-card ess-kpi-highlight">
            <div class="ess-kpi-label">Platform Monthly Recurring Revenue</div>
            <div class="ess-kpi-value">${formatCurrency(m.mrrCents)}</div>
            <div class="ess-kpi-meta">${m.activeTenants} active paying restaurant tenants</div>
          </div>
          <div class="ess-card ess-kpi-card">
            <div class="ess-kpi-label">Total Registered Tenants</div>
            <div class="ess-kpi-value">${m.totalTenants}</div>
            <div class="ess-kpi-meta">Multi-tenant PostgreSQL RLS isolation</div>
          </div>
          <div class="ess-card ess-kpi-card">
            <div class="ess-kpi-label">Locked / Suspended Accounts</div>
            <div class="ess-kpi-value">${m.lockedTenants}</div>
            <div class="ess-kpi-meta">Auto-unlocked upon M-Pesa webhook</div>
          </div>
          <div class="ess-card ess-kpi-card">
            <div class="ess-kpi-label">Transactional Outbox Queue</div>
            <div class="ess-kpi-value">${m.outboxPending} Pending</div>
            <div class="ess-kpi-meta">Background worker polling every 3s</div>
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header" style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem;">
            <div>
              <h3 class="ess-card-title">Provisioned Restaurant Tenants Directory</h3>
              <span class="ess-badge ess-badge-orange" style="margin-top:4px;">${allTenants.length} Enrolled Tenants</span>
            </div>
            <div style="display:flex; gap:0.5rem; flex-wrap:wrap; align-items:center;">
              <input type="text" id="sa-search-tenants" class="ess-input" placeholder="Search by name or slug..." value="${escapeHtml(filterText)}" style="width:220px;" />
              <select id="sa-filter-status" class="ess-select" style="width:130px;">
                <option value="ALL" ${filterStatus === 'ALL' ? 'selected' : ''}>All Statuses</option>
                <option value="ACTIVE" ${filterStatus === 'ACTIVE' ? 'selected' : ''}>Active</option>
                <option value="GRACE_PERIOD" ${filterStatus === 'GRACE_PERIOD' ? 'selected' : ''}>Grace Period</option>
                <option value="LOCKED_UNPAID" ${filterStatus === 'LOCKED_UNPAID' ? 'selected' : ''}>Locked</option>
                <option value="SUSPENDED" ${filterStatus === 'SUSPENDED' ? 'selected' : ''}>Suspended</option>
              </select>
              <select id="sa-filter-plan" class="ess-select" style="width:160px;">
                <option value="ALL" ${filterPlan === 'ALL' ? 'selected' : ''}>All Plans</option>
                <option value="STARTER" ${filterPlan === 'STARTER' ? 'selected' : ''}>Standard (30 Tokens)</option>
                <option value="PROFESSIONAL" ${filterPlan === 'PROFESSIONAL' ? 'selected' : ''}>Pro (30 Tokens)</option>
                <option value="ENTERPRISE" ${filterPlan === 'ENTERPRISE' ? 'selected' : ''}>Enterprise (30 Tokens)</option>
              </select>
            </div>
          </div>

          <div class="ess-table-wrap">
            <table class="ess-table">
              <thead>
                <tr>
                  <th>Restaurant &amp; Brand</th>
                  <th>Tenant Slug</th>
                  <th>Plan Tier</th>
                  <th>Tokens &amp; Access (24h/Token)</th>
                  <th>Structure</th>
                  <th>Account Status</th>
                  <th>Governance &amp; Token Actions</th>
                </tr>
              </thead>
              <tbody id="sa-tenants-tbody">
                ${renderTableRows(getFilteredTenants())}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

    bindEvents();
  }

  function bindEvents() {
    viewport.querySelector('#btn-onboard-restaurant')?.addEventListener('click', () => {
      openOnboardRestaurantModal(params.onRefresh);
    });

    viewport.querySelector('#btn-onboard-empty')?.addEventListener('click', () => {
      openOnboardRestaurantModal(params.onRefresh);
    });

    viewport.querySelector('#btn-refresh-tenants')?.addEventListener('click', () => {
      params.onRefresh();
    });

    const searchInput = viewport.querySelector('#sa-search-tenants') as HTMLInputElement | null;
    searchInput?.addEventListener('input', (e: any) => {
      filterText = (e.target.value || '').trim().toLowerCase();
      updateTableOnly();
    });

    const statusSelect = viewport.querySelector('#sa-filter-status') as HTMLSelectElement | null;
    statusSelect?.addEventListener('change', (e: any) => {
      filterStatus = e.target.value;
      updateTableOnly();
    });

    const planSelect = viewport.querySelector('#sa-filter-plan') as HTMLSelectElement | null;
    planSelect?.addEventListener('change', (e: any) => {
      filterPlan = e.target.value;
      updateTableOnly();
    });

    bindRowActions();
  }

  function updateTableOnly() {
    const tbody = viewport.querySelector('#sa-tenants-tbody');
    if (tbody) {
      tbody.innerHTML = renderTableRows(getFilteredTenants());
      bindRowActions();
    }
  }

  function bindRowActions() {
    // Status update
    viewport.querySelectorAll('[data-sa-tenant]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const tenantId = btn.getAttribute('data-sa-tenant') || '';
        const status = btn.getAttribute('data-sa-status') as any;
        try {
          await apiClient.updatePlatformTenantStatus(tenantId, status);
          showToast({
            title: 'Tenant Status Updated',
            message: `Tenant status changed to ${status}`,
            type: 'success',
          });
          params.onRefresh();
        } catch (err: any) {
          showToast({ title: 'Update Failed', message: err?.message, type: 'danger' });
        }
      });
    });

    // Grant / Top-Up Tokens
    viewport.querySelectorAll('[data-sa-grant-tokens]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tenantId = btn.getAttribute('data-sa-grant-tokens') || '';
        const tenantName = btn.getAttribute('data-sa-tenant-name') || 'Restaurant';
        const currentTokens = Number(btn.getAttribute('data-sa-current-tokens')) || 0;
        openGrantTokensModal(tenantId, tenantName, currentTokens, params.onRefresh);
      });
    });

    // Assign Branch
    viewport.querySelectorAll('[data-sa-add-branch]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tenantId = btn.getAttribute('data-sa-add-branch') || '';
        const tenantName = btn.getAttribute('data-sa-tenant-name') || 'Restaurant';
        openAssignBranchModal(tenantId, tenantName, params.onRefresh);
      });
    });

    // Assign Staff
    viewport.querySelectorAll('[data-sa-add-staff]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tenantId = btn.getAttribute('data-sa-add-staff') || '';
        const tenantName = btn.getAttribute('data-sa-tenant-name') || 'Restaurant';
        openAssignStaffModal(tenantId, tenantName, params.onRefresh);
      });
    });

    // EasyMenu 2-Way Connection & 1-Click Sync
    viewport.querySelectorAll('[data-sa-easymenu]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tenantId = btn.getAttribute('data-sa-easymenu') || '';
        const tenantName = btn.getAttribute('data-sa-tenant-name') || 'Restaurant';
        const slug = btn.getAttribute('data-sa-easymenu-slug') || '';
        const secretKey = btn.getAttribute('data-sa-secret-key') || '';
        openEasyMenuConnectModal(tenantId, tenantName, slug, params.onRefresh, secretKey);
      });
    });

    // Direct Navigate to Tenant Menu & EasyMenu Sync
    viewport.querySelectorAll('[data-sa-manage-menu]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tenantId = btn.getAttribute('data-sa-manage-menu') || '';
        try {
          apiClient.setSuperAdminTargetTenant(tenantId);
          apiClient.setActiveWorkspace('OWNER');
          window.location.reload();
        } catch (err: any) {
          showToast({ title: 'Navigation Failed', message: err?.message, type: 'danger' });
        }
      });
    });

    // Switch Target Tenant Context
    viewport.querySelectorAll('[data-sa-switch-tenant]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tenantId = btn.getAttribute('data-sa-switch-tenant') || '';
        try {
          apiClient.setSuperAdminTargetTenant(tenantId);
          showToast({
            title: 'Tenant Context Switched',
            message: `Target tenant set to ${tenantId}. Workspaces will now view this tenant data.`,
            type: 'info',
          });
        } catch (err: any) {
          showToast({ title: 'Context Switch Failed', message: err?.message, type: 'danger' });
        }
      });
    });

    // Delete Tenant
    viewport.querySelectorAll('[data-sa-delete-tenant]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const tenantId = btn.getAttribute('data-sa-delete-tenant') || '';
        const tenantName = btn.getAttribute('data-sa-tenant-name') || 'Restaurant';
        if (!confirm(`Are you sure you want to delete and unassign restaurant tenant "${tenantName}"? This action removes all tenant data.`)) {
          return;
        }
        try {
          await apiClient.superAdminDeleteTenant(tenantId);
          showToast({
            title: 'Restaurant Tenant Removed',
            message: `Tenant "${tenantName}" was successfully removed.`,
            type: 'success',
          });
          params.onRefresh();
        } catch (err: any) {
          showToast({ title: 'Deletion Failed', message: err?.message, type: 'danger' });
        }
      });
    });
  }

  renderView();
}

/**
 * Super Admin Full-Screen Add Restaurant View
 */
function renderSuperAdminAddRestaurantScreen(
  viewport: HTMLElement,
  params: {
    onSuccess: () => void;
    onCancel: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg" style="max-width:920px;margin:0 auto;padding-bottom:50px;">
      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;padding-bottom:14px;border-bottom:1px solid var(--ess-border-color);margin-bottom:8px;">
        <div>
          <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="sar-back-btn" style="padding:0;margin-bottom:6px;color:var(--ess-primary);font-weight:600;display:inline-flex;align-items:center;gap:4px;">
            &larr; Back to Enrolled Tenants
          </button>
          <h2 class="ess-section-title" style="margin:0;font-size:1.6rem;font-weight:800;color:var(--ess-text-primary);">+ Onboard New Restaurant Tenant</h2>
          <p class="ess-section-desc" style="margin:4px 0 0;font-size:0.9rem;">Provision isolated restaurant tenant, subscription plan, branch code, and initial staff account</p>
        </div>
        <div style="display:flex;align-items:center;gap:12px;">
          <button type="button" class="ess-btn ess-btn-secondary" id="sar-top-cancel-btn" style="padding:10px 18px;font-weight:600;">Cancel</button>
          <button type="submit" form="sar-onboard-form" class="ess-btn ess-btn-primary" id="sar-top-submit-btn" style="background:var(--ess-orange-primary,#f97316);color:#fff;font-weight:800;font-size:14px;padding:11px 24px;box-shadow:0 4px 14px rgba(249,115,22,0.35);border:none;border-radius:8px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            ✅ Confirm &amp; Issue Restaurant Tenant
          </button>
        </div>
      </div>

      <div class="ess-card" style="box-shadow:0 4px 20px rgba(0,0,0,0.06);border-radius:12px;">
        <form id="sar-onboard-form" class="ess-form">
          <div id="sar-error-box" style="display:none;margin-bottom:16px;" class="ess-alert ess-alert-danger"></div>

          <div style="border-bottom:1px solid var(--ess-border-color); padding-bottom:0.75rem; margin-bottom:1rem;">
            <h4 style="margin:0 0 4px 0; color:var(--ess-text-primary); font-size:1rem; font-weight:700;">1. Restaurant &amp; Brand Identity</h4>
            <span style="font-size:0.8rem; color:var(--ess-text-secondary);">Core multi-tenant configuration and subscription tier</span>
          </div>

          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">Restaurant Business Name *</label>
              <input type="text" id="sar-biz-name" class="ess-input" placeholder="e.g. Mombasa Coastal Bistro" required />
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Tenant Slug *</label>
              <input type="text" id="sar-biz-slug" class="ess-input" placeholder="mombasa-bistro" required />
              <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">Lowercase letters, numbers, and hyphens only</div>
            </div>
          </div>

          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">SaaS Subscription Tier *</label>
              <select id="sar-plan-code" class="ess-select" required>
                <option value="STARTER" selected>Standard Single Branch &mdash; KES 3,000/mo (30 Tokens &bull; Max 10 Staff)</option>
                <option value="PROFESSIONAL">2-Branch Restaurant &mdash; KES 6,000/mo (60 Tokens &bull; Max 20 Staff)</option>
                <option value="ENTERPRISE">Multi-Branch Cluster &mdash; KES 3,000/branch/mo (30 Tokens/branch &bull; Max 10 Staff/Branch)</option>
              </select>
              <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">Rate: KES 3,000 per branch (30 Tokens &bull; 720 hours &bull; Max 10 staff users per branch)</div>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Operating Currency *</label>
              <select id="sar-currency" class="ess-select" required>
                <option value="KES" selected>KES &mdash; Kenyan Shilling</option>
                <option value="USD">USD &mdash; US Dollar</option>
                <option value="EUR">EUR &mdash; Euro</option>
                <option value="GBP">GBP &mdash; British Pound</option>
              </select>
            </div>
          </div>

          <div style="border-bottom:1px solid var(--ess-border-color); padding-bottom:0.75rem; margin-bottom:1rem; margin-top:1.25rem;">
            <h4 style="margin:0 0 4px 0; color:var(--ess-text-primary); font-size:1rem; font-weight:700;">2. Initial Staff Account &amp; Assigned Role</h4>
            <span style="font-size:0.8rem; color:var(--ess-text-secondary);">Select role (Cashier, Waiter, Manager, Stock Keeper, or Owner) and Fast Login PIN</span>
          </div>

          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">First Name *</label>
              <input type="text" id="sar-first-name" class="ess-input" placeholder="e.g. Francis" required />
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Last Name *</label>
              <input type="text" id="sar-last-name" class="ess-input" placeholder="e.g. Oduor" required />
            </div>
          </div>

          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">Staff Login Email *</label>
              <input type="email" id="sar-email" class="ess-input" placeholder="staff@restaurant.co.ke" required />
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Staff Phone Number</label>
              <input type="tel" id="sar-phone" class="ess-input" placeholder="254712345678" />
            </div>
          </div>

          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">Assigned Staff Role &amp; Workspace *</label>
              <select id="sar-staff-role" class="ess-select" required>
                <option value="CASHIER" selected>💵 Cashier (Cashier POS Checkout)</option>
                <option value="WAITER">🍽️ Waiter (Floor Tables &amp; Orders)</option>
                <option value="MANAGER">📊 Manager (Operational Controls &amp; Approvals)</option>
                <option value="INVENTORY_CLERK">📦 Stock Keeper (Inventory &amp; Ledger)</option>
                <option value="OWNER">👑 Restaurant Owner (Owner Suite &amp; Admin)</option>
              </select>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Fast Login PIN (4 Digits) *</label>
              <input type="text" id="sar-staff-pin" class="ess-input" maxlength="6" value="1234" placeholder="1234" required />
              <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">4-digit PIN for instant terminal sign-in</div>
            </div>
          </div>

          <div class="ess-form-group">
            <label class="ess-label" style="display:flex; justify-content:space-between; align-items:center;">
              <span>Initial Password *</span>
              <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="sar-btn-gen-pass" style="font-size:0.75rem; padding:0 4px; color:var(--ess-orange-primary);">Generate Strong Password</button>
            </label>
            <input type="text" id="sar-password" class="ess-input" value="Password123!" required />
          </div>

          <div style="border-bottom:1px solid var(--ess-border-color); padding-bottom:0.75rem; margin-bottom:1rem; margin-top:1.25rem;">
            <h4 style="margin:0 0 4px 0; color:var(--ess-text-primary); font-size:1rem; font-weight:700;">3. Main Branch &amp; EasyMenu Link</h4>
            <span style="font-size:0.8rem; color:var(--ess-text-secondary);">Initial physical branch outlet and easy-scan.shop digital menu connection</span>
          </div>

          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">Main Branch Name</label>
              <input type="text" id="sar-branch-name" class="ess-input" placeholder="e.g. Main Store / Westlands Branch" />
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Branch Code</label>
              <input type="text" id="sar-branch-code" class="ess-input" placeholder="MAIN" value="MAIN" maxlength="10" />
            </div>
          </div>

          <div class="ess-form-group">
            <label class="ess-label">EasyMenu Digital Menu Slug (easy-scan.shop)</label>
            <div style="display:flex; align-items:center; gap:4px;">
              <span style="color:var(--ess-text-muted); font-size:0.85rem; background:var(--ess-bg-hover); padding:0.5rem; border:1px solid var(--ess-border-color); border-radius:6px 0 0 6px;">https://easy-scan.shop/r/</span>
              <input type="text" id="sar-easymenu-slug" class="ess-input" placeholder="mombasa-bistro" style="border-radius:0 6px 6px 0;" />
            </div>
            <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">QR table ordering submissions from this menu will automatically route to this POS</div>
          </div>

          <div style="margin-top:28px;padding:22px;background:var(--ess-bg-subtle,#f8fafc);border:2px dashed var(--ess-orange-primary,#f97316);border-radius:12px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;box-shadow:0 4px 16px rgba(0,0,0,0.04);">
            <div style="max-width:560px;">
              <div style="font-size:1.05rem;font-weight:800;color:var(--ess-text-primary);display:flex;align-items:center;gap:8px;">
                <span>🚀 Ready to Issue &amp; Provision Restaurant Tenant?</span>
              </div>
              <div style="font-size:0.85rem;color:var(--ess-text-secondary);margin-top:6px;line-height:1.45;">
                Provisions isolated database tenant, allocates <strong>30 Daily Tokens</strong> (KES 3,000/mo), configures branch pairing PIN (<code>12345654321</code>), and enables immediate staff sign-in.
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
              <button type="button" class="ess-btn ess-btn-secondary" id="sar-cancel-btn" style="padding:12px 20px;font-weight:600;">Cancel</button>
              <button type="submit" form="sar-onboard-form" class="ess-btn ess-btn-primary" id="sar-submit-btn" style="background:var(--ess-orange-primary,#f97316);color:#fff;font-weight:800;font-size:15px;padding:13px 30px;box-shadow:0 6px 18px rgba(249,115,22,0.4);border:none;border-radius:8px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                ✅ Confirm &amp; Issue Restaurant Tenant
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  `;

  const bizNameInput = viewport.querySelector('#sar-biz-name') as HTMLInputElement;
  const bizSlugInput = viewport.querySelector('#sar-biz-slug') as HTMLInputElement;
  const easyMenuInput = viewport.querySelector('#sar-easymenu-slug') as HTMLInputElement;

  bizNameInput?.addEventListener('input', () => {
    const slug = bizNameInput.value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    bizSlugInput.value = slug;
    if (easyMenuInput) easyMenuInput.value = slug;
  });

  viewport.querySelector('#sar-btn-gen-pass')?.addEventListener('click', () => {
    const passInput = viewport.querySelector('#sar-password') as HTMLInputElement;
    if (passInput) passInput.value = generateStrongPassword();
  });

  const backBtn = viewport.querySelector('#sar-back-btn') as HTMLButtonElement;
  const topCancelBtn = viewport.querySelector('#sar-top-cancel-btn') as HTMLButtonElement;
  const bottomCancelBtn = viewport.querySelector('#sar-cancel-btn') as HTMLButtonElement;

  backBtn?.addEventListener('click', params.onCancel);
  topCancelBtn?.addEventListener('click', params.onCancel);
  bottomCancelBtn?.addEventListener('click', params.onCancel);

  const form = viewport.querySelector('#sar-onboard-form') as HTMLFormElement;
  const errorBox = viewport.querySelector('#sar-error-box') as HTMLElement;
  const topSubmitBtn = viewport.querySelector('#sar-top-submit-btn') as HTMLButtonElement;
  const bottomSubmitBtn = viewport.querySelector('#sar-submit-btn') as HTMLButtonElement;

  const setButtonsLoading = (loading: boolean) => {
    const spinnerHtml = `<span class="ess-spinner-sm" style="display:inline-block;margin-right:6px;"></span>`;
    const checkSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>`;
    if (topSubmitBtn) {
      topSubmitBtn.disabled = loading;
      topSubmitBtn.innerHTML = loading ? `${spinnerHtml} Provisioning...` : `${checkSvg} ✅ Confirm &amp; Issue Restaurant Tenant`;
    }
    if (bottomSubmitBtn) {
      bottomSubmitBtn.disabled = loading;
      bottomSubmitBtn.innerHTML = loading ? `${spinnerHtml} Provisioning Restaurant Tenant...` : `${checkSvg} ✅ Confirm &amp; Issue Restaurant Tenant`;
    }
  };

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (errorBox) errorBox.style.display = 'none';

    const businessName = bizNameInput.value.trim();
    const businessSlug = bizSlugInput.value.trim().toLowerCase();
    const planCode = (viewport.querySelector('#sar-plan-code') as HTMLSelectElement).value;
    const currency = (viewport.querySelector('#sar-currency') as HTMLSelectElement).value;
    const firstName = (viewport.querySelector('#sar-first-name') as HTMLInputElement).value.trim();
    const lastName = (viewport.querySelector('#sar-last-name') as HTMLInputElement).value.trim();
    const email = (viewport.querySelector('#sar-email') as HTMLInputElement).value.trim();
    const phone = (viewport.querySelector('#sar-phone') as HTMLInputElement).value.trim();
    const password = (viewport.querySelector('#sar-password') as HTMLInputElement).value;
    const pin = (viewport.querySelector('#sar-staff-pin') as HTMLInputElement)?.value.trim() || '1234';
    const role = (viewport.querySelector('#sar-staff-role') as HTMLSelectElement)?.value || 'CASHIER';
    const branchName = (viewport.querySelector('#sar-branch-name') as HTMLInputElement).value.trim() || `${businessName} Main`;
    const branchCode = (viewport.querySelector('#sar-branch-code') as HTMLInputElement).value.trim().toUpperCase() || 'MAIN';
    const easyMenuSlug = easyMenuInput.value.trim().toLowerCase() || businessSlug;

    if (!businessName || !businessSlug || !firstName || !lastName || !email || !password) {
      if (errorBox) {
        errorBox.textContent = 'Please fill out all required fields.';
        errorBox.style.display = 'block';
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) || email.includes('/')) {
      if (errorBox) {
        errorBox.innerHTML = `<strong>Invalid Email Address:</strong> '<code>${escapeHtml(email)}</code>' is not a valid email address. Please remove any slashes (e.g. use <code>bilalmandake@gmail.com</code> or <code>bilalmanda.ke@gmail.com</code>).`;
        errorBox.style.display = 'block';
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      (viewport.querySelector('#sar-email') as HTMLInputElement)?.focus();
      return;
    }

    setButtonsLoading(true);

    try {
      const res = await apiClient.superAdminOnboardTenant({
        businessName,
        businessSlug,
        planCode,
        currency,
        firstName,
        lastName,
        email,
        phone: phone || undefined,
        password,
        pin,
        role: role as any,
        branchName,
        branchCode,
        easyMenuSlug,
      });

      showToast({
        title: 'Restaurant Provisioned',
        message: `${businessName} successfully created!`,
        type: 'success',
      });

      openOnboardingSuccessModal(res);
      params.onSuccess();
    } catch (err: any) {
      if (errorBox) {
        const detailMsg = Array.isArray(err?.details) && err.details.length > 0
          ? err.details.map((d: any) => `&bull; <strong>${escapeHtml(d.path || 'Validation')}:</strong> ${escapeHtml(d.message)}`).join('<br>')
          : escapeHtml(err?.message || 'Failed to onboard restaurant tenant. Check slug/email uniqueness.');
        errorBox.innerHTML = `<div style="font-weight:700;margin-bottom:4px;">Cannot Provision Restaurant:</div>${detailMsg}`;
        errorBox.style.display = 'block';
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setButtonsLoading(false);
    }
  });
}

/**
 * Super Admin Restaurant Onboarding Wizard Modal
 */
export function openOnboardRestaurantModal(onSuccess: () => void) {
  openModal({
    title: 'Onboard & Assign New Restaurant Tenant',
    subtitle: 'Provisions new isolated tenant, initial branch, stock location, owner account and subscription',
    width: 'lg',
    bodyHtml: `
      <form id="onboard-tenant-form" class="ess-form">
        <div id="onboard-error-box" style="display:none;" class="ess-alert ess-alert-danger"></div>

        <div style="border-bottom:1px solid var(--ess-border-color); padding-bottom:0.75rem; margin-bottom:1rem;">
          <h4 style="margin:0 0 4px 0; color:var(--ess-text-primary); font-size:0.95rem;">1. Restaurant &amp; Brand Identity</h4>
          <span style="font-size:0.8rem; color:var(--ess-text-secondary);">Core multi-tenant configuration and subscription tier</span>
        </div>

        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Restaurant Business Name *</label>
            <input type="text" id="ob-biz-name" class="ess-input" placeholder="e.g. Carnivore Grill &amp; Lounge" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Tenant Slug *</label>
            <input type="text" id="ob-biz-slug" class="ess-input" placeholder="carnivore-grill" required />
            <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">Lowercase letters, numbers, and hyphens only</div>
          </div>
        </div>

        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">SaaS Subscription Tier *</label>
            <select id="ob-plan-code" class="ess-select" required>
              <option value="STARTER" selected>Standard Single Branch &mdash; KES 3,000/mo (30 Tokens &bull; Max 10 Staff)</option>
              <option value="PROFESSIONAL">2-Branch Restaurant &mdash; KES 6,000/mo (60 Tokens &bull; Max 20 Staff)</option>
              <option value="ENTERPRISE">Multi-Branch Cluster &mdash; KES 3,000/branch/mo (30 Tokens/branch &bull; Max 10 Staff/Branch)</option>
            </select>
            <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">Rate: KES 3,000 per branch (30 Tokens &bull; 720 hours &bull; Max 10 staff users per branch)</div>
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Operating Currency *</label>
            <select id="ob-currency" class="ess-select" required>
              <option value="KES" selected>KES &mdash; Kenyan Shilling</option>
              <option value="USD">USD &mdash; US Dollar</option>
              <option value="EUR">EUR &mdash; Euro</option>
              <option value="GBP">GBP &mdash; British Pound</option>
            </select>
          </div>
        </div>

        <div style="border-bottom:1px solid var(--ess-border-color); padding-bottom:0.75rem; margin-bottom:1rem; margin-top:1.25rem;">
          <h4 style="margin:0 0 4px 0; color:var(--ess-text-primary); font-size:0.95rem;">2. Initial Staff Account &amp; Assigned Role</h4>
          <span style="font-size:0.8rem; color:var(--ess-text-secondary);">Select role (Cashier, Waiter, Manager, Stock Keeper, or Owner) and Fast Login PIN</span>
        </div>

        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">First Name *</label>
            <input type="text" id="ob-first-name" class="ess-input" placeholder="e.g. Francis" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Last Name *</label>
            <input type="text" id="ob-last-name" class="ess-input" placeholder="e.g. Oduor" required />
          </div>
        </div>

        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Staff Login Email *</label>
            <input type="email" id="ob-email" class="ess-input" placeholder="staff@carnivore.co.ke" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Staff Phone Number</label>
            <input type="tel" id="ob-phone" class="ess-input" placeholder="254712345678" />
          </div>
        </div>

        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Assigned Staff Role &amp; Workspace *</label>
            <select id="ob-staff-role" class="ess-select" required>
              <option value="CASHIER" selected>💵 Cashier (Cashier POS Checkout)</option>
              <option value="WAITER">🍽️ Waiter (Floor Tables &amp; Orders)</option>
              <option value="MANAGER">📊 Manager (Operational Controls &amp; Approvals)</option>
              <option value="INVENTORY_CLERK">📦 Stock Keeper (Inventory &amp; Ledger)</option>
              <option value="OWNER">👑 Restaurant Owner (Owner Suite &amp; Admin)</option>
            </select>
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Fast Login PIN (4 Digits) *</label>
            <input type="text" id="ob-staff-pin" class="ess-input" maxlength="6" value="1234" placeholder="1234" required />
            <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">4-digit PIN for instant terminal sign-in</div>
          </div>
        </div>

        <div class="ess-form-group">
          <label class="ess-label" style="display:flex; justify-content:space-between; align-items:center;">
            <span>Initial Password *</span>
            <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="btn-gen-password" style="font-size:0.75rem; padding:0 4px; color:var(--ess-orange-primary);">Generate Strong Password</button>
          </label>
          <input type="text" id="ob-password" class="ess-input" value="Password123!" required />
        </div>

        <div style="border-bottom:1px solid var(--ess-border-color); padding-bottom:0.75rem; margin-bottom:1rem; margin-top:1.25rem;">
          <h4 style="margin:0 0 4px 0; color:var(--ess-text-primary); font-size:0.95rem;">3. Main Branch &amp; EasyMenu Link</h4>
          <span style="font-size:0.8rem; color:var(--ess-text-secondary);">Initial physical branch outlet and easy-scan.shop digital menu connection</span>
        </div>

        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Main Branch Name</label>
            <input type="text" id="ob-branch-name" class="ess-input" placeholder="e.g. Main Store / Westlands Branch" />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Branch Code</label>
            <input type="text" id="ob-branch-code" class="ess-input" placeholder="MAIN" value="MAIN" maxlength="10" />
          </div>
        </div>

        <div class="ess-form-group">
          <label class="ess-label">EasyMenu Digital Menu Slug (easy-scan.shop)</label>
          <div style="display:flex; align-items:center; gap:4px;">
            <span style="color:var(--ess-text-muted); font-size:0.85rem; background:var(--ess-bg-hover); padding:0.5rem; border:1px solid var(--ess-border-color); border-radius:6px 0 0 6px;">https://easy-scan.shop/r/</span>
            <input type="text" id="ob-easymenu-slug" class="ess-input" placeholder="carnivore-grill" style="border-radius:0 6px 6px 0;" />
          </div>
          <div style="font-size:0.75rem; color:var(--ess-text-muted); margin-top:2px;">QR table ordering submissions from this menu will automatically route to this POS</div>
        </div>
      </form>
    `,
    footerHtml: `
      <div style="display:flex; justify-content:space-between; align-items:center; width:100%; gap:0.5rem;">
        <button type="button" class="ess-btn ess-btn-secondary" id="ob-cancel-btn">Cancel</button>
        <button type="submit" form="onboard-tenant-form" class="ess-btn ess-btn-primary" id="ob-submit-btn" style="background:var(--ess-orange-primary,#f97316); color:#fff; font-weight:800; font-size:14px; padding:10px 22px; box-shadow:0 3px 10px rgba(249,115,22,0.3); border:none; border-radius:6px; display:inline-flex; align-items:center; gap:6px;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          ✅ Confirm &amp; Issue Restaurant Tenant
        </button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const form = modalRoot.querySelector('#onboard-tenant-form') as HTMLFormElement;
      const bizNameInput = modalRoot.querySelector('#ob-biz-name') as HTMLInputElement;
      const bizSlugInput = modalRoot.querySelector('#ob-biz-slug') as HTMLInputElement;
      const easyMenuInput = modalRoot.querySelector('#ob-easymenu-slug') as HTMLInputElement;
      const branchNameInput = modalRoot.querySelector('#ob-branch-name') as HTMLInputElement;
      const passwordInput = modalRoot.querySelector('#ob-password') as HTMLInputElement;
      const genPassBtn = modalRoot.querySelector('#btn-gen-password') as HTMLButtonElement;
      const submitBtn = modalRoot.querySelector('#ob-submit-btn') as HTMLButtonElement;
      const cancelBtn = modalRoot.querySelector('#ob-cancel-btn') as HTMLButtonElement;
      const errorBox = modalRoot.querySelector('#onboard-error-box') as HTMLElement;

      cancelBtn?.addEventListener('click', close);

      // Auto slugify name
      bizNameInput?.addEventListener('input', () => {
        const slug = bizNameInput.value
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        if (!bizSlugInput.dataset.manual) {
          bizSlugInput.value = slug;
          easyMenuInput.value = slug;
        }
        if (!branchNameInput.dataset.manual) {
          branchNameInput.value = bizNameInput.value ? `${bizNameInput.value} - Main Store` : '';
        }
      });

      bizSlugInput?.addEventListener('input', () => {
        bizSlugInput.dataset.manual = 'true';
        easyMenuInput.value = bizSlugInput.value;
      });

      branchNameInput?.addEventListener('input', () => {
        branchNameInput.dataset.manual = 'true';
      });

      // Password generator
      genPassBtn?.addEventListener('click', () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
        let pass = '';
        for (let i = 0; i < 12; i++) {
          pass += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        passwordInput.value = pass;
      });

      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorBox.style.display = 'none';

        const businessName = bizNameInput.value.trim();
        const businessSlug = bizSlugInput.value.trim().toLowerCase();
        const planCode = (modalRoot.querySelector('#ob-plan-code') as HTMLSelectElement).value as any;
        const currency = (modalRoot.querySelector('#ob-currency') as HTMLSelectElement).value;
        const role = (modalRoot.querySelector('#ob-staff-role') as HTMLSelectElement).value as any;
        const pin = (modalRoot.querySelector('#ob-staff-pin') as HTMLInputElement).value.trim() || '1234';
        const firstName = (modalRoot.querySelector('#ob-first-name') as HTMLInputElement).value.trim();
        const lastName = (modalRoot.querySelector('#ob-last-name') as HTMLInputElement).value.trim();
        const email = (modalRoot.querySelector('#ob-email') as HTMLInputElement).value.trim().toLowerCase();
        const phone = (modalRoot.querySelector('#ob-phone') as HTMLInputElement).value.trim();
        const password = passwordInput.value;
        const branchName = branchNameInput.value.trim() || `${businessName} - Main Store`;
        const branchCode = ((modalRoot.querySelector('#ob-branch-code') as HTMLInputElement).value.trim() || 'MAIN').toUpperCase();
        const easyMenuSlug = easyMenuInput.value.trim().toLowerCase() || businessSlug;

        if (!businessName || !businessSlug || !firstName || !lastName || !email || !password) {
          errorBox.textContent = 'Please fill in all required fields marked with *';
          errorBox.style.display = 'block';
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email) || email.includes('/')) {
          errorBox.innerHTML = `<strong>Invalid Email Address:</strong> '<code>${escapeHtml(email)}</code>' is not a valid email address. Please remove any slashes (e.g. use <code>bilalmandake@gmail.com</code> or <code>bilalmanda.ke@gmail.com</code>).`;
          errorBox.style.display = 'block';
          (modalRoot.querySelector('#ob-email') as HTMLInputElement)?.focus();
          return;
        }

        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="ess-spinner-sm" style="display:inline-block; margin-right:6px;"></span> Provisioning...';

        try {
          const res = await apiClient.superAdminOnboardTenant({
            businessName,
            businessSlug,
            planCode,
            currency,
            role,
            pin,
            firstName,
            lastName,
            email,
            phone: phone || undefined,
            password,
            branchName,
            branchCode,
            easyMenuSlug,
          });

          close();
          onSuccess();

          // Display success credentials modal
          openOnboardingSuccessModal(res);
        } catch (err: any) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg> ✅ Confirm &amp; Issue Restaurant Tenant';
          const detailMsg = Array.isArray(err?.details) && err.details.length > 0
            ? err.details.map((d: any) => `&bull; <strong>${escapeHtml(d.path || 'Validation')}:</strong> ${escapeHtml(d.message)}`).join('<br>')
            : escapeHtml(err?.message || 'Failed to onboard restaurant tenant. Check slug/email uniqueness.');
          errorBox.innerHTML = `<div style="font-weight:700;margin-bottom:4px;">Cannot Provision Restaurant:</div>${detailMsg}`;
          errorBox.style.display = 'block';
        }
      });
    },
  });
}

/**
 * Super Admin Onboarding Celebratory Credentials Modal
 */
function openOnboardingSuccessModal(data: any) {
  const creds = data.credentials || {};
  const tenant = data.tenant || {};
  const owner = data.owner || {};
  const plan = data.plan || {};
  const secretKey = data.secretKey || creds.secretKey || tenant.secret_key || tenant.secretKey || `ess_sec_${(tenant.slug || 'restaurant').replace(/[^a-z0-9]/g, '_')}_${(tenant.id || '').slice(0, 8)}`;

  openModal({
    title: 'Restaurant Provisioned & Assigned Successfully!',
    subtitle: 'The tenant workspace, initial branch, and credentials are live and ready',
    width: 'md',
    bodyHtml: `
      <div class="ess-stack-md">
        <div class="ess-alert ess-alert-success" style="display:flex; align-items:center; gap:8px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <div>
            <strong>${escapeHtml(tenant.name)}</strong> (<code>${escapeHtml(tenant.slug)}</code>) has been onboarded on the <strong>${escapeHtml(plan.name || 'ESS Plan')}</strong>.
          </div>
        </div>

        <div class="ess-card" style="background:var(--ess-bg-hover); border:1px solid var(--ess-border-color);">
          <div style="font-weight:600; font-size:0.85rem; color:var(--ess-text-secondary); margin-bottom:8px; text-transform:uppercase; letter-spacing:0.5px;">Owner Authentication &amp; EasyMenu Sync Credentials</div>
          <div class="ess-kv-list">
            <div class="ess-kv-row"><span>Owner Name:</span><strong>${escapeHtml(`${owner.firstName} ${owner.lastName}`)}</strong></div>
            <div class="ess-kv-row"><span>Login Email:</span><strong><code>${escapeHtml(creds.email)}</code></strong></div>
            <div class="ess-kv-row"><span>Password:</span><strong><code>${escapeHtml(creds.password)}</code></strong></div>
            <div class="ess-kv-row"><span>Token Balance:</span><strong><span class="ess-badge ess-badge-orange">30 Tokens (720 Hours Active)</span></strong></div>
            <div class="ess-kv-row"><span>Token Consumption:</span><span>1 Token = 24 Hours POS access (KES 3,000 / month)</span></div>
            <div class="ess-kv-row"><span>POS Station:</span><a href="https://pos.easy-scan.shop" target="_blank" style="color:var(--ess-orange-primary);">https://pos.easy-scan.shop</a></div>
            <div class="ess-kv-row"><span>EasyMenu Menu:</span><a href="${escapeHtml(creds.easyMenuUrl)}" target="_blank" style="color:var(--ess-orange-primary);">${escapeHtml(creds.easyMenuUrl)}</a></div>
            <div class="ess-kv-row"><span>EasyMenu Secret Key:</span><strong><code style="color:var(--ess-orange-primary);font-weight:700;">${escapeHtml(secretKey)}</code></strong></div>
            <div class="ess-kv-row"><span>POS Ingestion Endpoint:</span><code>https://pos.easy-scan.shop/api/v1/qr-orders/submit</code></div>
          </div>
        </div>
      </div>
    `,
    footerHtml: `
      <div style="display:flex; justify-content:space-between; width:100%; align-items:center;">
        <button type="button" class="ess-btn ess-btn-secondary" id="btn-copy-creds">
          Copy Credentials &amp; Key
        </button>
        <button type="button" class="ess-btn ess-btn-primary" id="btn-done-creds" style="background:var(--ess-orange-primary); color:#fff;">
          Done
        </button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      modalRoot.querySelector('#btn-done-creds')?.addEventListener('click', close);

      modalRoot.querySelector('#btn-copy-creds')?.addEventListener('click', () => {
        const text = `ESS POS & Operations Platform
Restaurant: ${tenant.name} (${tenant.slug})
Owner: ${owner.firstName} ${owner.lastName}
Login Email: ${creds.email}
Password: ${creds.password}
POS Station URL: ${creds.posLoginUrl || 'https://pos.easy-scan.shop'}
Digital Menu URL: ${creds.easyMenuUrl || 'https://easy-scan.shop'}
EasyMenu Secret Key: ${secretKey}
POS Webhook URL: https://pos.easy-scan.shop/api/v1/qr-orders/submit`;

        navigator.clipboard.writeText(text).then(() => {
          showToast({ title: 'Credentials Copied', message: 'Credentials and secret key copied to clipboard', type: 'success' });
        });
      });
    },
  });
}

/**
 * Super Admin EasyMenu Connection & Live Sync Modal
 */
export function openEasyMenuConnectModal(
  tenantId: string,
  tenantName: string,
  initialSlug: string,
  onRefresh: () => void,
  initialSecretKey?: string
) {
  const currentSlug = initialSlug || 'sip-chat-cafe';
  const secretKey = initialSecretKey || `ess_sec_${currentSlug.replace(/[^a-z0-9]/g, '_')}_${tenantId.slice(0, 8)}`;
  openModal({
    title: `⚡ EasyMenu Connection — ${escapeHtml(tenantName)}`,
    subtitle: 'Synchronize dishes, prices & photos from easy-scan.shop and route live QR table orders',
    width: 'md',
    bodyHtml: `
      <div class="ess-stack-md">
        <div class="ess-card" style="background:var(--ess-bg-hover);border:1px solid var(--ess-border-color);padding:16px;">
          <div style="font-size:0.85rem;font-weight:700;color:var(--ess-text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            Live Digital Menu Connection &amp; Secret Key
          </div>
          <div class="ess-form-group" style="margin-bottom:12px;">
            <label class="ess-label">EasyMenu Digital Menu Slug (easy-scan.shop)</label>
            <div style="display:flex;align-items:center;gap:4px;">
              <span style="color:var(--ess-text-muted);font-size:0.85rem;background:var(--ess-bg-subtle);padding:0.5rem;border:1px solid var(--ess-border-color);border-radius:6px 0 0 6px;">https://easy-scan.shop/r/</span>
              <input type="text" id="em-sync-slug" class="ess-input" value="${escapeHtml(currentSlug)}" style="border-radius:0 6px 6px 0;" />
            </div>
            <div style="margin-top:8px;display:flex;gap:12px;align-items:center;">
              <a href="https://easy-scan.shop/r/${escapeHtml(currentSlug)}" target="_blank" rel="noopener noreferrer" id="em-live-link" style="color:var(--ess-orange-primary);font-size:0.85rem;text-decoration:none;font-weight:700;display:inline-flex;align-items:center;gap:4px;">
                Open EasyMenu QR Catalog &nearr;
              </a>
            </div>
          </div>

          <div class="ess-form-group" style="margin-bottom:12px;">
            <label class="ess-label">Restaurant Secret Integration Key (copy to EasyMenu)</label>
            <div style="display:flex;align-items:center;gap:6px;">
              <input type="text" readonly id="em-secret-key-input" class="ess-input" value="${escapeHtml(secretKey)}" style="font-family:monospace;font-size:0.85rem;background:var(--ess-bg-card,#fff);cursor:pointer;" />
              <button type="button" class="ess-btn ess-btn-secondary" id="em-copy-secret-btn" style="white-space:nowrap;font-weight:700;">
                📋 Copy Key
              </button>
            </div>
          </div>

          <div style="font-size:0.75rem;color:var(--ess-text-secondary);line-height:1.45;background:rgba(0,0,0,0.02);padding:10px;border-radius:6px;border:1px solid var(--ess-border-color);">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
              <strong>POS Webhook Ingestion Endpoint:</strong>
              <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="em-copy-endpoint-btn" style="padding:2px 6px;font-size:11px;color:var(--ess-primary);font-weight:700;">📋 Copy URL</button>
            </div>
            <code style="color:var(--ess-primary);">https://pos.easy-scan.shop/api/v1/qr-orders/submit</code><br>
            QR code table orders placed on easy-scan.shop route in real time into this restaurant's Cashier &amp; Waiter queue using this secret key.
          </div>
        </div>

        <div id="em-sync-status-box" style="display:none;" class="ess-alert"></div>

        <div class="ess-card" style="padding:16px;border:2px dashed var(--ess-orange-primary);background:rgba(249,115,22,0.03);">
          <div style="font-weight:800;font-size:0.95rem;margin-bottom:6px;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            <span>⚡ 1-Click Menu &amp; High-Res Food Photo Synchronization</span>
          </div>
          <p style="font-size:0.8rem;color:var(--ess-text-secondary);margin:0 0 14px;line-height:1.4;">
            Pulls all live categories (Single Espresso, Double Cappuccino, Grill &amp; Steaks, Pastries), prices, and S3 photography directly into <strong>${escapeHtml(tenantName)}</strong> POS catalog.
          </p>
          <button type="button" class="ess-btn ess-btn-primary" id="em-do-sync-btn" style="background:var(--ess-orange-primary);color:#fff;font-weight:800;width:100%;padding:12px;font-size:14px;box-shadow:0 4px 12px rgba(249,115,22,0.3);">
            ⚡ Synchronize Dishes &amp; Photos to POS
          </button>
        </div>
      </div>
    `,
    footerHtml: `
      <div style="display:flex;justify-content:space-between;width:100%;align-items:center;">
        <button type="button" class="ess-btn ess-btn-secondary" id="em-close-btn">Close</button>
        <button type="button" class="ess-btn ess-btn-ghost" id="em-open-owner-btn" style="color:var(--ess-primary);font-weight:700;">
          🍽️ Open Restaurant Dishes in POS &rarr;
        </button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const syncBtn = modalRoot.querySelector('#em-do-sync-btn') as HTMLButtonElement;
      const slugInput = modalRoot.querySelector('#em-sync-slug') as HTMLInputElement;
      const statusBox = modalRoot.querySelector('#em-sync-status-box') as HTMLElement;
      const liveLink = modalRoot.querySelector('#em-live-link') as HTMLAnchorElement;
      const closeBtn = modalRoot.querySelector('#em-close-btn') as HTMLButtonElement;
      const openOwnerBtn = modalRoot.querySelector('#em-open-owner-btn') as HTMLButtonElement;

      closeBtn?.addEventListener('click', close);

      modalRoot.querySelector('#em-copy-secret-btn')?.addEventListener('click', () => {
        navigator.clipboard.writeText(secretKey).then(() => {
          showToast({ title: 'Secret Key Copied', message: 'Secret key copied to clipboard', type: 'success' });
        });
      });

      modalRoot.querySelector('#em-copy-endpoint-btn')?.addEventListener('click', () => {
        navigator.clipboard.writeText('https://pos.easy-scan.shop/api/v1/qr-orders/submit').then(() => {
          showToast({ title: 'Endpoint Copied', message: 'POS Webhook URL copied to clipboard', type: 'success' });
        });
      });

      slugInput?.addEventListener('input', () => {
        const slug = slugInput.value.trim().toLowerCase();
        if (liveLink) {
          liveLink.href = `https://easy-scan.shop/r/${slug}`;
        }
      });

      openOwnerBtn?.addEventListener('click', () => {
        close();
        apiClient.setSuperAdminTargetTenant(tenantId);
        apiClient.setActiveWorkspace('OWNER');
        window.location.reload();
      });

      syncBtn?.addEventListener('click', async () => {
        const slug = slugInput.value.trim().toLowerCase();
        if (!slug) {
          if (statusBox) {
            statusBox.className = 'ess-alert ess-alert-danger';
            statusBox.textContent = 'Please enter an EasyMenu slug';
            statusBox.style.display = 'block';
          }
          return;
        }

        syncBtn.disabled = true;
        syncBtn.innerHTML = '<span class="ess-spinner-sm" style="display:inline-block;margin-right:6px;"></span> Synchronizing Dishes &amp; Photos...';
        if (statusBox) statusBox.style.display = 'none';

        try {
          apiClient.setSuperAdminTargetTenant(tenantId);
          const res = await apiClient.importEasyMenu(slug);

          if (statusBox) {
            statusBox.className = 'ess-alert ess-alert-success';
            statusBox.innerHTML = `<strong>✅ Sync Successful!</strong><br>${escapeHtml(res.message || 'Menu items and high-res food photos synchronized.')}`;
            statusBox.style.display = 'block';
          }

          showToast({
            title: 'EasyMenu Synced',
            message: `${tenantName} updated with latest dishes & photos!`,
            type: 'success',
          });

          syncBtn.disabled = false;
          syncBtn.innerHTML = '⚡ Re-Sync Dishes &amp; Photos to POS';
          onRefresh();
        } catch (err: any) {
          syncBtn.disabled = false;
          syncBtn.innerHTML = '⚡ Synchronize Dishes &amp; Photos to POS';
          if (statusBox) {
            statusBox.className = 'ess-alert ess-alert-danger';
            statusBox.textContent = err?.message || 'Failed to synchronize with EasyMenu';
            statusBox.style.display = 'block';
          }
        }
      });
    },
  });
}

/**
 * Super Admin Assign Additional Branch Modal
 */
function openAssignBranchModal(tenantId: string, tenantName: string, onSuccess: () => void) {
  openModal({
    title: `Assign Branch to ${escapeHtml(tenantName)}`,
    subtitle: 'Creates an additional branch and default stock location',
    width: 'md',
    bodyHtml: `
      <form id="assign-branch-form" class="ess-form">
        <div id="branch-error-box" style="display:none;" class="ess-alert ess-alert-danger"></div>
        <div class="ess-form-group">
          <label class="ess-label">Branch Name *</label>
          <input type="text" id="br-name" class="ess-input" placeholder="e.g. Westlands Mall Branch" required />
        </div>
        <div class="ess-form-group">
          <label class="ess-label">Branch Code (2-10 chars) *</label>
          <input type="text" id="br-code" class="ess-input" placeholder="WST02" required maxlength="10" />
        </div>
        <div class="ess-form-group">
          <label class="ess-label">Physical Address</label>
          <input type="text" id="br-address" class="ess-input" placeholder="e.g. 2nd Floor, Westgate Mall" />
        </div>
        <div class="ess-form-group">
          <label class="ess-label">Contact Phone</label>
          <input type="tel" id="br-phone" class="ess-input" placeholder="2547XXXXXXXX" />
        </div>
      </form>
    `,
    footerHtml: `
      <div style="display:flex; justify-content:flex-end; gap:0.5rem; width:100%;">
        <button type="button" class="ess-btn ess-btn-secondary" id="br-cancel-btn">Cancel</button>
        <button type="submit" form="assign-branch-form" class="ess-btn ess-btn-primary" id="br-submit-btn">Assign Branch</button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const form = modalRoot.querySelector('#assign-branch-form') as HTMLFormElement;
      const submitBtn = modalRoot.querySelector('#br-submit-btn') as HTMLButtonElement;
      const errorBox = modalRoot.querySelector('#branch-error-box') as HTMLElement;

      modalRoot.querySelector('#br-cancel-btn')?.addEventListener('click', close);

      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = (modalRoot.querySelector('#br-name') as HTMLInputElement).value.trim();
        const code = (modalRoot.querySelector('#br-code') as HTMLInputElement).value.trim().toUpperCase();
        const address = (modalRoot.querySelector('#br-address') as HTMLInputElement).value.trim();
        const phone = (modalRoot.querySelector('#br-phone') as HTMLInputElement).value.trim();

        if (!name || !code) return;

        submitBtn.disabled = true;
        submitBtn.textContent = 'Assigning...';

        try {
          await apiClient.superAdminCreateBranch(tenantId, { name, code, address: address || undefined, phone: phone || undefined });
          showToast({ title: 'Branch Assigned', message: `Branch "${name}" created successfully.`, type: 'success' });
          close();
          onSuccess();
        } catch (err: any) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Assign Branch';
          errorBox.textContent = err?.message || 'Failed to assign branch';
          errorBox.style.display = 'block';
        }
      });
    },
  });
}

/**
 * Super Admin Assign Staff Modal
 */
function openAssignStaffModal(tenantId: string, tenantName: string, onSuccess: () => void) {
  openModal({
    title: `Assign Staff to ${escapeHtml(tenantName)}`,
    subtitle: 'Creates a manager, cashier, waiter, or inventory clerk account for this restaurant',
    width: 'md',
    bodyHtml: `
      <form id="assign-staff-form" class="ess-form">
        <div id="staff-error-box" style="display:none;" class="ess-alert ess-alert-danger"></div>
        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">First Name *</label>
            <input type="text" id="as-first" class="ess-input" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Last Name *</label>
            <input type="text" id="as-last" class="ess-input" required />
          </div>
        </div>
        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Email *</label>
            <input type="email" id="as-email" class="ess-input" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Phone</label>
            <input type="tel" id="as-phone" class="ess-input" placeholder="2547XXXXXXXX" />
          </div>
        </div>
        <div class="ess-grid-3">
          <div class="ess-form-group">
            <label class="ess-label">Role *</label>
            <select id="as-role" class="ess-select" required>
              <option value="CASHIER" selected>💵 Cashier</option>
              <option value="WAITER">🍽️ Waiter</option>
              <option value="MANAGER">📊 Manager</option>
              <option value="INVENTORY_CLERK">📦 Stock Keeper</option>
              <option value="OWNER">👑 Restaurant Owner</option>
            </select>
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Fast Login PIN (4 Digits) *</label>
            <input type="text" id="as-pin" class="ess-input" maxlength="6" value="1234" placeholder="1234" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Password *</label>
            <input type="text" id="as-pass" class="ess-input" value="Password123!" required />
          </div>
        </div>
      </form>
    `,
    footerHtml: `
      <div style="display:flex; justify-content:flex-end; gap:0.5rem; width:100%;">
        <button type="button" class="ess-btn ess-btn-secondary" id="as-cancel-btn">Cancel</button>
        <button type="submit" form="assign-staff-form" class="ess-btn ess-btn-primary" id="as-submit-btn">Assign Staff Member</button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const form = modalRoot.querySelector('#assign-staff-form') as HTMLFormElement;
      const submitBtn = modalRoot.querySelector('#as-submit-btn') as HTMLButtonElement;
      const errorBox = modalRoot.querySelector('#staff-error-box') as HTMLElement;

      modalRoot.querySelector('#as-cancel-btn')?.addEventListener('click', close);

      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const firstName = (modalRoot.querySelector('#as-first') as HTMLInputElement).value.trim();
        const lastName = (modalRoot.querySelector('#as-last') as HTMLInputElement).value.trim();
        const email = (modalRoot.querySelector('#as-email') as HTMLInputElement).value.trim().toLowerCase();
        const phone = (modalRoot.querySelector('#as-phone') as HTMLInputElement).value.trim();
        const role = (modalRoot.querySelector('#as-role') as HTMLSelectElement).value as any;
        const pin = (modalRoot.querySelector('#as-pin') as HTMLInputElement)?.value.trim() || '1234';
        const password = (modalRoot.querySelector('#as-pass') as HTMLInputElement).value;

        if (!firstName || !lastName || !email || !password) return;

        submitBtn.disabled = true;
        submitBtn.textContent = 'Assigning...';

        try {
          await apiClient.superAdminCreateStaff(tenantId, { firstName, lastName, email, phone: phone || undefined, role, password, pin });
          showToast({ title: 'Staff Member Assigned', message: `${firstName} ${lastName} added as ${role}.`, type: 'success' });
          close();
          onSuccess();
        } catch (err: any) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Assign Staff Member';
          errorBox.textContent = err?.message || 'Failed to assign staff member';
          errorBox.style.display = 'block';
        }
      });
    },
  });
}

/**
 * Super Admin Grant / Top-Up Operational Tokens Modal
 * Model: KES 3,000 / month = 30 Tokens (1 Token = 24 Hours active POS access)
 */
function openGrantTokensModal(tenantId: string, tenantName: string, currentTokens: number, onSuccess: () => void) {
  openModal({
    title: `Grant Operational Tokens &bull; ${escapeHtml(tenantName)}`,
    subtitle: 'Subscription Model: KES 3,000/month = 30 Tokens (1 Token = 24 Hours POS access)',
    width: 'md',
    bodyHtml: `
      <form id="grant-tokens-form" class="ess-form">
        <div id="grant-error-box" style="display:none;" class="ess-alert ess-alert-danger"></div>

        <div class="ess-card" style="background:var(--ess-bg-hover); padding:12px; margin-bottom:12px; border:1px solid var(--ess-border-color);">
          <div class="ess-kv-row"><span>Current Token Balance:</span><strong>${currentTokens} Tokens (${currentTokens * 24} Hours)</strong></div>
          <div class="ess-kv-row"><span>Daily Token Consumption:</span><span>1 Token consumes exactly 24 hours of operational time</span></div>
          <div class="ess-kv-row"><span>Monthly Standard:</span><span>KES 3,000 / Branch = 30 Tokens (Max 10 Staff Users per Branch)</span></div>
        </div>

        <div class="ess-form-group">
          <label class="ess-label">Select Token Package to Credit *</label>
          <select id="gt-package" class="ess-select" required>
            <option value="30" selected>30 Tokens &mdash; KES 3,000 (Full Month &bull; 720 Hours)</option>
            <option value="60">60 Tokens &mdash; KES 6,000 (2 Months &bull; 1,440 Hours)</option>
            <option value="15">15 Tokens &mdash; KES 1,500 (15 Days &bull; 360 Hours)</option>
            <option value="7">7 Tokens &mdash; KES 700 (1 Week &bull; 168 Hours)</option>
            <option value="custom">Custom Token Quantity...</option>
          </select>
        </div>

        <div class="ess-form-group" id="gt-custom-group" style="display:none;">
          <label class="ess-label">Custom Number of Tokens (Each = 24h)</label>
          <input type="number" id="gt-custom-tokens" class="ess-input" min="1" max="365" value="30" />
        </div>
      </form>
    `,
    footerHtml: `
      <div style="display:flex; justify-content:flex-end; gap:0.5rem; width:100%;">
        <button type="button" class="ess-btn ess-btn-secondary" id="gt-cancel-btn">Cancel</button>
        <button type="submit" form="grant-tokens-form" class="ess-btn ess-btn-primary" id="gt-submit-btn" style="background:var(--ess-orange-primary); color:#fff; font-weight:600;">
          Credit Tokens &amp; Extend Access
        </button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const form = modalRoot.querySelector('#grant-tokens-form') as HTMLFormElement;
      const pkgSelect = modalRoot.querySelector('#gt-package') as HTMLSelectElement;
      const customGroup = modalRoot.querySelector('#gt-custom-group') as HTMLElement;
      const customInput = modalRoot.querySelector('#gt-custom-tokens') as HTMLInputElement;
      const submitBtn = modalRoot.querySelector('#gt-submit-btn') as HTMLButtonElement;
      const errorBox = modalRoot.querySelector('#grant-error-box') as HTMLElement;

      modalRoot.querySelector('#gt-cancel-btn')?.addEventListener('click', close);

      pkgSelect?.addEventListener('change', () => {
        if (pkgSelect.value === 'custom') {
          customGroup.style.display = 'block';
        } else {
          customGroup.style.display = 'none';
        }
      });

      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const tokens = pkgSelect.value === 'custom' ? Number(customInput.value) : Number(pkgSelect.value);
        if (!tokens || tokens <= 0) return;

        submitBtn.disabled = true;
        submitBtn.textContent = 'Crediting Tokens...';

        try {
          await apiClient.superAdminGrantTokens(tenantId, tokens);
          close();
          showToast({
            title: 'Tokens Credited Successfully',
            message: `Credited ${tokens} tokens (${tokens * 24} hours). Restaurant account is ACTIVE.`,
            type: 'success',
          });
          onSuccess();
        } catch (err: any) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Credit Tokens & Extend Access';
          errorBox.textContent = err?.message || 'Failed to grant tokens';
          errorBox.style.display = 'block';
        }
      });
    },
  });
}



function renderSuperAdminPlansScreen(
  viewport: HTMLElement,
  params: {
    overview: any;
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">SaaS Subscription Plans &amp; Payment Webhook Reconciliation</h2>
          <p class="ess-section-desc">Manage platform tiers and trigger manual reconciliation for stale M-Pesa payment attempts</p>
        </div>
        <button type="button" class="ess-btn ess-btn-primary" id="sa-reconcile-btn">
          Run Stale Payment Reconciliation
        </button>
      </div>

      <div class="ess-grid-3">
        ${(params.overview.plans || [])
          .map(
            (p: any) => `
          <div class="ess-card">
            <div class="ess-card-header">
              <h3 class="ess-card-title">${escapeHtml(p.name)}</h3>
              <span class="ess-badge ess-badge-orange">${escapeHtml(p.code)}</span>
            </div>
            <div style="font-size:24px;font-weight:700;margin-bottom:8px;">${formatCurrency(p.monthly_price_cents)}<span style="font-size:13px;font-weight:400;">/month</span></div>
            <p style="font-size:13px;color:var(--ess-text-secondary);margin-bottom:12px;">${escapeHtml(p.description || '')}</p>
            <div class="ess-kv-row"><span>Pricing Model</span><strong>KES 3,000 per Branch / Month</strong></div>
            <div class="ess-kv-row"><span>Token Quota</span><strong>30 Tokens per Branch (24h/Token)</strong></div>
            <div class="ess-kv-row"><span>Staff Limit</span><strong>Max 10 Users per Branch</strong></div>
            <div class="ess-kv-row"><span>Max Branches</span><strong>${p.max_branches}</strong></div>
          </div>
        `
          )
          .join('')}
      </div>
    </div>
  `;

  viewport.querySelector('#sa-reconcile-btn')?.addEventListener('click', async () => {
    try {
      const res = await apiClient.reconcilePayments();
      showToast({
        title: 'Payment Reconciliation Complete',
        message: `Checked pending payment attempts (${res.length} stale records processed).`,
        type: 'success',
      });
    } catch (err: any) {
      showToast({ title: 'Reconciliation Error', message: err?.message, type: 'danger' });
    }
  });
}

function renderSuperAdminHealthScreen(
  viewport: HTMLElement,
  params: {
    health: any;
    overview: any;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Platform Health, API Status &amp; Distributed Architecture</h2>
          <p class="ess-section-desc">Stateless API cluster readiness for Contabo / 4-Server deployment</p>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Live API Engine Probe (/health)</h3>
            <span class="ess-badge ess-badge-success">${escapeHtml(params.health?.status || 'UP')}</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row"><span>Engine Name</span><strong>${escapeHtml(params.health?.system)}</strong></div>
            <div class="ess-kv-row"><span>Release Version</span><strong>v${escapeHtml(params.health?.version)}</strong></div>
            <div class="ess-kv-row"><span>Process Uptime</span><strong>${params.health?.uptimeSeconds ?? 0} seconds</strong></div>
            <div class="ess-kv-row"><span>Probe Timestamp</span><strong>${formatDateTime(params.health?.timestamp)}</strong></div>
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">4-Server Distributed Deployment Readiness</h3>
            <span class="ess-badge ess-badge-orange">Stateless Ready</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row"><span>1. Reverse Proxy &amp; TLS</span><strong>Nginx Rate-Limited Load Balancer</strong></div>
            <div class="ess-kv-row"><span>2. Stateless API Instances</span><strong>Node.js LTS + Strict TypeScript</strong></div>
            <div class="ess-kv-row"><span>3. Authoritative Database</span><strong>PostgreSQL 16 + PgBouncer Pooler</strong></div>
            <div class="ess-kv-row"><span>4. Cache, Workers &amp; Media</span><strong>Redis + Outbox Workers + S3/R2 Storage</strong></div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderSuperAdminSupportScreen(
  viewport: HTMLElement,
  params: {
    overview: any;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Customer Account Support &amp; Platform Security Audit</h2>
          <p class="ess-section-desc">Cross-tenant spoofing protection and platform-level governance events</p>
        </div>
      </div>

      <div class="ess-card">
        <div class="ess-card-header">
          <h3 class="ess-card-title">Platform Security &amp; Tenant Isolation Guarantees</h3>
          <span class="ess-badge ess-badge-success">Enforced</span>
        </div>
        <div class="ess-stack-sm">
          <div class="ess-kv-row">
            <span>Cross-Tenant Header Spoofing Protection</span>
            <span class="ess-badge ess-badge-success">HTTP 403 TENANT_FORBIDDEN Enforced</span>
          </div>
          <div class="ess-kv-row">
            <span>PostgreSQL Row-Level Security (RLS)</span>
            <span class="ess-badge ess-badge-success">app.current_tenant_id Session Bound</span>
          </div>
          <div class="ess-kv-row">
            <span>Unpaid Subscription Lockout</span>
            <span class="ess-badge ess-badge-success">HTTP 402 PAYMENT_REQUIRED on Mutations</span>
          </div>
        </div>
      </div>
    </div>
  `;
}

